/*
 * VNCtalk - an enterprise real-time communication solution including chat, video and audio conferencing, screen sharing, voice messaging, file sharing, broadcasts, document collaboration and much more.
 * Copyright (C) 2015-2020 VNC – Virtual Network Consult AG (info@vnc.biz)
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, version 3 of the License.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. Look for COPYING file in the top folder.
 * If not, see http://www.gnu.org/licenses/.
 */

import { environment } from "../../environments/environment";
import { ContactAddress, TelInformation, EmailInformation } from "../models/vcard.model";
import * as html from "html-escaper";
import { TranslateService } from "@ngx-translate/core";
import { AppSettings } from "../models/app-settings.model";
import { InjectionToken } from "@angular/core";
import { BehaviorSubject, from, Observable, Observer, Subject, take } from "rxjs";
import { Md5 } from "ts-md5/dist/md5";
import * as entities from "entities";
import toPlaintext from "quill-delta-to-plaintext";
import * as mime from "mime-types";
import * as jwt from "jsonwebtoken";
import { ConstantsUtil } from "./constants.util";
import { CallParticipantStats } from "../models/call-participant-stats.model";
import SimpleCrypto from "simple-crypto-js";
import getBrowserFingerprint from "get-browser-fingerprint";
import { htmlToText } from "html-to-text";
import {CommentItem} from "@uxf/vnc-library";
import {Channel} from "../../channels/models/channel.model";
import {CommentResponse} from "../../channels/models/comment.model";
import {HttpEvent, HttpEventType, HttpProgressEvent, HttpResponse, HttpHeaders} from "@angular/common/http";
import {Upload} from "../../channels/models/upload.model";
import {Topic} from "../../channels/models/topic.model";

import Autolinker from "autolinker";
import { Post } from "../models/post.model";
import { Follow } from "../models/follow.model";

import format from "date-fns/format";
import startOfDay from "date-fns/startOfDay";
import startOfWeek from "date-fns/startOfWeek";
import endOfWeek from "date-fns/endOfWeek";
import subSeconds from "date-fns/subSeconds";
import subDays from "date-fns/subDays";
import subWeeks from "date-fns/subWeeks";
import parseISO from "date-fns/parseISO";
import isSameDay from "date-fns/isSameDay";
import isWithinInterval from "date-fns/isWithinInterval";
import differenceInSeconds from "date-fns/differenceInSeconds";
import differenceInMinutes from "date-fns/differenceInMinutes";
import differenceInHours from "date-fns/differenceInHours";
import differenceInDays from "date-fns/differenceInDays";
import differenceInWeeks from "date-fns/differenceInWeeks";
import differenceInMonths from "date-fns/differenceInMonths";
import differenceInYears from "date-fns/differenceInYears";
import { LoggerService } from "app/shared/services/logger.service";
import * as Sentry from "@sentry/browser";
import { Message } from "../models/message.model";

if (environment.enableSentry && environment.production) {
  Sentry.init({
    dsn: "https://ccff09193bae47d6afe18ccf6b6df3ad@sentry3.vnc.biz/3",
    maxBreadcrumbs: 150,
    debug: true,
  });
}

const secretKey = "6d65e128f84b474c83c7004445176498";
const simpleCrypto = new SimpleCrypto(secretKey);
// eslint-disable-next-line quotes
const QUOTE_START = '&amp;lt;body xmlns="<a class="open-new-window" href="http://www.w3.org/1999/xhtml&quot;&amp;gt" class="open-new-window" target="_blank" rel="noopener">http://www.w3.org/1999/xhtml"&amp;gt</a>;';
const EMAIL_REGEXP =
  /^(?=.{1,254}$)(?=.{1,64}@)[-!#$%&'*+/0-9=?A-Z^_`a-z{|}~]+(\.[-!#$%&'*+/0-9=?A-Z^_`a-z{|}~]+)*@[A-Za-z0-9]([A-Za-z0-9-]{0,61}[A-Za-z0-9])?(\.[A-Za-z0-9]([A-Za-z0-9-]{0,61}[A-Za-z0-9])?)*$/;
export const TEST_VALUE = new InjectionToken("test_value");

const NON_PRINTABLE_UNICODE_CHARACTERS = /[\x1B]/g;
// const NON_PRINTABLE_UNICODE_CHARACTERS = /[\0-\x1F\x7F-\x9F\xAD\u0378\u0379\u037F-\u0383\u038B\u038D\u03A2\u0528-\u0530\u0557\u0558\u0560\u0588\u058B-\u058E\u0590\u05C8-\u05CF\u05EB-\u05EF\u05F5-\u0605\u061C\u061D\u06DD\u070E\u070F\u074B\u074C\u07B2-\u07BF\u07FB-\u07FF\u082E\u082F\u083F\u085C\u085D\u085F-\u089F\u08A1\u08AD-\u08E3\u08FF\u0978\u0980\u0984\u098D\u098E\u0991\u0992\u09A9\u09B1\u09B3-\u09B5\u09BA\u09BB\u09C5\u09C6\u09C9\u09CA\u09CF-\u09D6\u09D8-\u09DB\u09DE\u09E4\u09E5\u09FC-\u0A00\u0A04\u0A0B-\u0A0E\u0A11\u0A12\u0A29\u0A31\u0A34\u0A37\u0A3A\u0A3B\u0A3D\u0A43-\u0A46\u0A49\u0A4A\u0A4E-\u0A50\u0A52-\u0A58\u0A5D\u0A5F-\u0A65\u0A76-\u0A80\u0A84\u0A8E\u0A92\u0AA9\u0AB1\u0AB4\u0ABA\u0ABB\u0AC6\u0ACA\u0ACE\u0ACF\u0AD1-\u0ADF\u0AE4\u0AE5\u0AF2-\u0B00\u0B04\u0B0D\u0B0E\u0B11\u0B12\u0B29\u0B31\u0B34\u0B3A\u0B3B\u0B45\u0B46\u0B49\u0B4A\u0B4E-\u0B55\u0B58-\u0B5B\u0B5E\u0B64\u0B65\u0B78-\u0B81\u0B84\u0B8B-\u0B8D\u0B91\u0B96-\u0B98\u0B9B\u0B9D\u0BA0-\u0BA2\u0BA5-\u0BA7\u0BAB-\u0BAD\u0BBA-\u0BBD\u0BC3-\u0BC5\u0BC9\u0BCE\u0BCF\u0BD1-\u0BD6\u0BD8-\u0BE5\u0BFB-\u0C00\u0C04\u0C0D\u0C11\u0C29\u0C34\u0C3A-\u0C3C\u0C45\u0C49\u0C4E-\u0C54\u0C57\u0C5A-\u0C5F\u0C64\u0C65\u0C70-\u0C77\u0C80\u0C81\u0C84\u0C8D\u0C91\u0CA9\u0CB4\u0CBA\u0CBB\u0CC5\u0CC9\u0CCE-\u0CD4\u0CD7-\u0CDD\u0CDF\u0CE4\u0CE5\u0CF0\u0CF3-\u0D01\u0D04\u0D0D\u0D11\u0D3B\u0D3C\u0D45\u0D49\u0D4F-\u0D56\u0D58-\u0D5F\u0D64\u0D65\u0D76-\u0D78\u0D80\u0D81\u0D84\u0D97-\u0D99\u0DB2\u0DBC\u0DBE\u0DBF\u0DC7-\u0DC9\u0DCB-\u0DCE\u0DD5\u0DD7\u0DE0-\u0DF1\u0DF5-\u0E00\u0E3B-\u0E3E\u0E5C-\u0E80\u0E83\u0E85\u0E86\u0E89\u0E8B\u0E8C\u0E8E-\u0E93\u0E98\u0EA0\u0EA4\u0EA6\u0EA8\u0EA9\u0EAC\u0EBA\u0EBE\u0EBF\u0EC5\u0EC7\u0ECE\u0ECF\u0EDA\u0EDB\u0EE0-\u0EFF\u0F48\u0F6D-\u0F70\u0F98\u0FBD\u0FCD\u0FDB-\u0FFF\u10C6\u10C8-\u10CC\u10CE\u10CF\u1249\u124E\u124F\u1257\u1259\u125E\u125F\u1289\u128E\u128F\u12B1\u12B6\u12B7\u12BF\u12C1\u12C6\u12C7\u12D7\u1311\u1316\u1317\u135B\u135C\u137D-\u137F\u139A-\u139F\u13F5-\u13FF\u169D-\u169F\u16F1-\u16FF\u170D\u1715-\u171F\u1737-\u173F\u1754-\u175F\u176D\u1771\u1774-\u177F\u17DE\u17DF\u17EA-\u17EF\u17FA-\u17FF\u180F\u181A-\u181F\u1878-\u187F\u18AB-\u18AF\u18F6-\u18FF\u191D-\u191F\u192C-\u192F\u193C-\u193F\u1941-\u1943\u196E\u196F\u1975-\u197F\u19AC-\u19AF\u19CA-\u19CF\u19DB-\u19DD\u1A1C\u1A1D\u1A5F\u1A7D\u1A7E\u1A8A-\u1A8F\u1A9A-\u1A9F\u1AAE-\u1AFF\u1B4C-\u1B4F\u1B7D-\u1B7F\u1BF4-\u1BFB\u1C38-\u1C3A\u1C4A-\u1C4C\u1C80-\u1CBF\u1CC8-\u1CCF\u1CF7-\u1CFF\u1DE7-\u1DFB\u1F16\u1F17\u1F1E\u1F1F\u1F46\u1F47\u1F4E\u1F4F\u1F58\u1F5A\u1F5C\u1F5E\u1F7E\u1F7F\u1FB5\u1FC5\u1FD4\u1FD5\u1FDC\u1FF0\u1FF1\u1FF5\u1FFF\u200B-\u200F\u202A-\u202E\u2060-\u206F\u2072\u2073\u208F\u209D-\u209F\u20BB-\u20CF\u20F1-\u20FF\u218A-\u218F\u23F4-\u23FF\u2427-\u243F\u244B-\u245F\u2700\u2B4D-\u2B4F\u2B5A-\u2BFF\u2C2F\u2C5F\u2CF4-\u2CF8\u2D26\u2D28-\u2D2C\u2D2E\u2D2F\u2D68-\u2D6E\u2D71-\u2D7E\u2D97-\u2D9F\u2DA7\u2DAF\u2DB7\u2DBF\u2DC7\u2DCF\u2DD7\u2DDF\u2E3C-\u2E7F\u2E9A\u2EF4-\u2EFF\u2FD6-\u2FEF\u2FFC-\u2FFF\u3040\u3097\u3098\u3100-\u3104\u312E-\u3130\u318F\u31BB-\u31BF\u31E4-\u31EF\u321F\u32FF\u4DB6-\u4DBF\u9FCD-\u9FFF\uA48D-\uA48F\uA4C7-\uA4CF\uA62C-\uA63F\uA698-\uA69E\uA6F8-\uA6FF\uA78F\uA794-\uA79F\uA7AB-\uA7F7\uA82C-\uA82F\uA83A-\uA83F\uA878-\uA87F\uA8C5-\uA8CD\uA8DA-\uA8DF\uA8FC-\uA8FF\uA954-\uA95E\uA97D-\uA97F\uA9CE\uA9DA-\uA9DD\uA9E0-\uA9FF\uAA37-\uAA3F\uAA4E\uAA4F\uAA5A\uAA5B\uAA7C-\uAA7F\uAAC3-\uAADA\uAAF7-\uAB00\uAB07\uAB08\uAB0F\uAB10\uAB17-\uAB1F\uAB27\uAB2F-\uABBF\uABEE\uABEF\uABFA-\uABFF\uD7A4-\uD7AF\uD7C7-\uD7CA\uD7FC-\uF8FF\uFA6E\uFA6F\uFADA-\uFAFF\uFB07-\uFB12\uFB18-\uFB1C\uFB37\uFB3D\uFB3F\uFB42\uFB45\uFBC2-\uFBD2\uFD40-\uFD4F\uFD90\uFD91\uFDC8-\uFDEF\uFDFE\uFDFF\uFE1A-\uFE1F\uFE27-\uFE2F\uFE53\uFE67\uFE6C-\uFE6F\uFE75\uFEFD-\uFF00\uFFBF-\uFFC1\uFFC8\uFFC9\uFFD0\uFFD1\uFFD8\uFFD9\uFFDD-\uFFDF\uFFE7\uFFEF-\uFFFB\uFFFE\uFFFF]/g;
export const APK_STORE_URL = "https://newpackages.vnc.biz/apk/";
export const APP_VERSION_REGEXP = "\\d\.\\d\.\\d\.\\d+";

export class CommonUtil {
  static showCircularMenu(element: any) {
    const avatar = element.querySelector(".vnc-avatar").getBoundingClientRect();

    let heightShiftDelta = 0;
    let widthShiftDelta = 0;
    if (document.querySelector(".wrapper-size-xs")) {
      heightShiftDelta = 46;
      widthShiftDelta = 46;
    } else if (document.querySelector(".wrapper-size-s")) {
      heightShiftDelta = 52;
      widthShiftDelta = 52;
    } else if (document.querySelector(".wrapper-size-m")) {
      heightShiftDelta = 56;
      widthShiftDelta = 56;
    } else {
      LoggerService.error("[showCircularMenu] Wrapper size selector not found");
    }

    (<HTMLElement> document.querySelector(".circular-avatar-wrapper")).style.top = (avatar.top) + "px";
    (<HTMLElement> document.querySelector(".circular-avatar-wrapper")).style.left = (avatar.left)  + "px";
    (<HTMLElement> document.querySelector(".circular-avatar-wrapper")).style.marginTop = ((avatar.height / 2) - heightShiftDelta)  + "px";
    if (CommonUtil.isOnMobileDevice() && document.querySelector("vp-global-chat") !== null) {
      (<HTMLElement> document.querySelector(".circular-avatar-wrapper")).style.marginLeft = ((avatar.width / 2) - 30)  + "px";
    } else {
      (<HTMLElement> document.querySelector(".circular-avatar-wrapper")).style.marginLeft = ((avatar.width / 2) - widthShiftDelta)  + "px";
    }
    document.querySelector(".circular-avatar-wrapper").querySelector(".current-avatar").innerHTML = element.querySelector(".avatar").innerHTML;
    // LoggerService.info("[showCircularMenu]", element.querySelector(".vnc-avatar"), avatar);

    setTimeout(() => {
      (<HTMLElement> document.querySelector(".circular-avatar-wrapper")).style.visibility = "visible";
      (<HTMLElement> document.querySelector(".circular-avatar-wrapper")).style.display = "block";
    }, 200);
  }

  static OMEMO_MAX_DEVICES = 5;

  static hideCircularMenu() {
    if (!!document.querySelector(".circular-avatar-wrapper")
    && (<HTMLElement> document.querySelector(".circular-avatar-wrapper")).style.visibility === "visible") {
      (<HTMLElement> document.querySelector(".circular-avatar-wrapper")).style.visibility = "hidden";
      (<HTMLElement> document.querySelector(".circular-avatar-wrapper")).style.display = "none";
    }
  }
  // PRASHANT_COMMENT common helper methods here
  static imagesExtensions = ["jpeg", "jpg", "gif", "png", "bmp", "tiff", "webp", "svg"];
  static audioExtensions = ["mp3", "wav", "wma", "ogg", "aac", "flac", "aiff", "amr"];
  static videoExtensions = ["mp4", "mov", "wmv", "webm", "wma", "flv", "mov", "mpg", "avi", "3gp", "mkv"];
  static documentExtensions = ["csv",
    "txt",
    "xls",
    "xlsx",
    "doc",
    "docx",
    "html",
    "js",
    "pdf",
    "pptx",
    "ppt",
    "ods",
    "odt"];
  static supportedVideoExtensions = ["mp4", "webm", "mov", "ogv"];
  static supportedVCFExtensions = ["vcf"];

  static zipExtensions = ["7z", "zip", "rar", "tar", "gz", "jar", "gz"];
  static unsupportVideoExtensions = ["flv" , "3gp" , "hevc" , "m2v" , "asf" , "avi", "f4v"];
  static DOWNLOAD_SIZE_LIMIT = 1000 * 1024 * 2;
  static USER_PALETTE = {
    A: "#FC9488",
    B: "#BF3425",
    C: "#A3A9FF",
    D: "#FC9488",
    E: "#BF3425",
    F: "#A3A9FF",
    G: "#FC9488",
    H: "#AEDB6A",
    I: "#F87838",
    J: "#FC9488",
    K: "#AEDB6A",
    L: "#64C8A7",
    M: "#FC9488",
    N: "#FFAD16",
    O: "#64C8A7",
    P: "#FC9488",
    Q: "#2A8E6D",
    R: "#1BA3B8",
    S: "#FC9488",
    T: "#2A8E6D",
    U: "#84B229",
    V: "#FC9488",
    W: "#2A8E6D",
    X: "#84B229",
    Y: "#FC9488",
    Z: "#E42E6B"
  };

  static decimalNcr = {
    ä: "&#228;" ,
    ö: "&#246;" ,
    ü: "&#252;" ,
    Ä: "&#196;" ,
    Ö: "&#214;" ,
    Ü: "&#220;" ,
    ß: "&#223;" ,
    ẞ: "&#7838;"
  };

  static langDict = {
    "de" : {
      photo : "Foto",
      audio : "Sprachnachricht",
      video : "Video",
      link  : "Link"
    },
    "en" : {
      photo : "Photo",
      video : "Video",
      audio : "Voice Message",
      link  : "Link"
    },
  };

  static FILE_TYPES_AVAILABLE_FOR_PREVIEW: string[] = [
    // spreadsheet types
    "xls", "xlsx", "ods", "csv", "xlst", "xlsy", "gsheet", "xlsm", "xlt", "xltm", "xltx", "fods", "ots",
    // presentation types
    "pps", "ppsx", "ppt", "pptx", "odp", "pptt", "ppty", "gslides", "pot", "potm", "potx", "ppsm", "pptm", "fodp", "otp",
    // text document types
    "doc", "docx", "doct", "odt", "gdoc", "txt", "rtf", "pdf", "mht", "htm", "html", "epub", "djvu", "xps", "oxps", "docm", "dot", "dotm", "dotx", "fodt", "ott", "fb2", "xml",
  ];

  static DEFAULT_PERMISSIONS = {
    "read_only": false,
    "moderate_channel": true,
    "editable": true,
  };

  static translateAppURLtoDeeplinkURL(url: string): string {
    let origURL = new URL(url);
    let deepLinkUrl = origURL.protocol + "//" + origURL.hostname + "/api/deeplink" + origURL.pathname + origURL.search;
    return deepLinkUrl;
  }

  static getURLwithoutPath(url: string): string {
    let origURL = new URL(url);
    let urlWithoutPath = origURL.protocol + "//" + origURL.hostname;
    return urlWithoutPath;
  }


  static translateHINFileURL(text: string): string {
    // LoggerService.info("[translateHINFileURL]: " + text);

    if (!text) {
      return;
    }

    let resultUrl = text;
    if (environment.theme === "hin") {

      let origURL = new URL(text);
      let isNotHIN = (origURL.hostname.indexOf("talk.hin.ch") === -1) && (origURL.hostname.indexOf("talk.hintest.ch") === -1);
      if (resultUrl.startsWith("file:") || resultUrl.startsWith("blob:") || isNotHIN) {
        LoggerService.info("[translateHINFileURL]: not translating " + text + " keep " + resultUrl);
        return resultUrl;
      }
      if (environment.isElectron || this.isOnNativeMobileDevice() || environment.isCordova) {
        // LoggerService.info("commonHINURL: electron/mobile translating: ", text);
        resultUrl = localStorage.getItem("serverURL") + origURL.pathname + origURL.search;
      } else {
        // LoggerService.info("[translateHINFileURL]: webclient translating: ", text);
        if ((window.location.hostname.indexOf("talk.hin.ch") > -1) || (window.location.hostname.indexOf("talk.hintest.ch") > -1)) {
          if ((window.location.port !== "3000") && (window.location.hostname.indexOf("localhost") > -1)) {
            resultUrl = "http://talk.hin.ch" + origURL.pathname + origURL.search;
          } else {
            resultUrl = window.location.protocol + "//" + window.location.host + origURL.pathname + origURL.search;
          }
        } else {
          resultUrl = text;
          if ((window.location.port !== "3000") && (window.location.hostname.indexOf("localhost") > -1)) {
            resultUrl = "http://talk.hin.ch" + origURL.pathname + origURL.search;
          }
        }
      }
    }
    // LoggerService.info("[translateHINFileURL]: translated " + text + " to " + resultUrl);
    return resultUrl;
  }

  static getAvatarBackground(text: string): string {
    return this.USER_PALETTE[text.charAt(0)];
  }

  static getAvatarText(text: string): string {
    if (!text) {
      return "";
    }
    text = text.split("@")[0].replace(/\./g, " ").replace(/\W-_+/g, "");
    const str = text.split(/\s+/);
    // if (str.length > 1) {
    //   return str[0].substr(0, 1).toUpperCase() + str[1].substr(0, 1).toUpperCase();
    // }
    return text.substr(0, 1).toUpperCase();
  }

  static modifyAvatarUrl( avatarUrl: string, size: 46|47|57|80|420) {
    if (!!size) {
      return avatarUrl.replace(".jpg", `-${size}.jpg`);
    } else {
      return avatarUrl;
    }
  }

  static randomId(length: number = 7, containNumbers: boolean = true) {
    let randomId = "";
    let dictionary = "abcdefghijklmnopqrstuvwxyz";

    if (containNumbers) {
      dictionary += "0123456789";
    }

    const dictionaryLength = dictionary.length;

    for (let i = 0; i < length; i++) {
      randomId += dictionary.charAt(Math.floor(Math.random() * dictionaryLength));
    }

    return randomId;
  }

  static getRandomNumbers() {
    let array = new Uint32Array(10);
    window.crypto.getRandomValues(array);
    return array.join("");
  }

  static escapeHTMLString(unescapedString: string): string {
    if (!unescapedString) {
      return "";
    }
    const escaped = html.escape(unescapedString);
    // LoggerService.info("[HTML][common.util][escapeHTMLString] escaped: ", escaped);
    return escaped;
  }

  static unEscapeHTMLString(str: string): string {
    // LoggerService.info("[unEscapeHTMLString]", typeof str);
    if (!str) {
      return "";
    }
    str = str.toString();
    const unescaped = html.unescape(str);
    // LoggerService.info("[HTML][common.util][unEscapeHTMLString] unescaped: ", unescaped);
    return unescaped;
  }

  static copyToClipboard(stringArray: string[]) {
    let str = stringArray.join("\n");
    if (environment.isCordova) {
      cordova.plugins.clipboard.copy(str);
    } else {
      window.Clipboard = (function (window, document, navigator) {
        let textArea,
          copy;

        function isIOS() {
          return navigator.userAgent.match(/ipad|iphone/i);
        }

        function createTextArea(text) {
          textArea = document.createElement("textArea");
          textArea.value = text;
          document.body.appendChild(textArea);
        }

        function selectText() {
          let range,
            selection;

          if (isIOS()) {
            range = document.createRange();
            range.selectNodeContents(textArea);
            selection = window.getSelection();
            selection.removeAllRanges();
            selection.addRange(range);
            textArea.setSelectionRange(0, 999999);
          } else {
            textArea.select();
          }
          LoggerService.info("[selectText]", textArea.innerHTML);
        }

        function copyToClipboard() {
          document.execCommand("copy");
          document.body.removeChild(textArea);
        }

        copy = function (text) {
          createTextArea(text);
          selectText();
          copyToClipboard();
          textArea = null;
        };

        return {
          copy: copy
        };
      })(window, document, navigator);
      window.Clipboard.copy(str);
    }
  }

  static escapeCarriagesAndNewline(unescapedString: string): string {
    if (!unescapedString) {
      return "";
    }
    return unescapedString.replace(/\r?\n/g, " ");
  }

  static hashCode(s: string) {
    if (!s) {
      return "";
    }
    return s.split("").reduce(function(a,b){a = ((a << 5) - a) + b.charCodeAt(0);return a & a;},0);
  }

  static generateCachedBody(messageBody, highlight?: string, skipEmoji?: boolean) {
    if (!messageBody) {
      // console.warn("[CommonUtil][generateCachedBody] empty message body, ignore1");
      return "";
    }
    if (messageBody.indexOf("meta_task_mention") !== -1 || messageBody.indexOf("ticket_mention") !== -1 || messageBody.indexOf("?jwt=REDMINE_JWT") !== -1) {
      return CommonUtil.generateCachedBodyRedmine(messageBody, highlight);
    }

    // const t0 = performance.now();

    messageBody = CommonUtil.processHTMLBody(messageBody);
    messageBody = CommonUtil.linkify(messageBody).replace(/\r?\n/g, "\\n").replace(/\\n/g, " <br />").replace(/user active/g, "open-new-window");
    if (highlight) {
      messageBody = CommonUtil.highlightSearch(messageBody, highlight);
    }
    if (!skipEmoji) {
      messageBody = CommonUtil.processEmoji(messageBody.replace(/<\/p>/ig, " </p>").replace(/<p>:/ig, "<p> :"));
    }

    // const t1 = performance.now();

    // save cached content
    return messageBody;
  }

  static getSnackbarOption() {
    let matbarOption = { duration: 2000, panelClass: "custom-snackbar-bottom-left" };
    if (CommonUtil.isMobileSize()) {
      matbarOption = { duration: 2000, panelClass: "custom-mobile-snackbar" };
    }
    return matbarOption;
  }

  static generateCachedBodyRedmine(messageBody, highlight?: string, skipEmoji?: boolean) {
    if (!messageBody) {
      // console.warn("[CommonUtil][generateCachedBody] empty message body, ignore1");
      return "";
    }
    const jwt = !!localStorage.getItem("jwtTemp") ? localStorage.getItem("jwtTemp") : "";
    messageBody = messageBody.replace(/\?jwt=REDMINE_JWT/g, "?jwt=" + jwt);
    messageBody = CommonUtil.processHTMLBody(messageBody);
    messageBody = CommonUtil.linkifyMe(messageBody).replace(/\r?\n/g, "\\n").replace(/\\n/g, " <br />");
    if (highlight) {
      messageBody = CommonUtil.highlightSearch(messageBody, highlight);
    }
    if (!skipEmoji) {
      messageBody = CommonUtil.processEmoji(messageBody.replace(/<\/p>/ig, " </p>").replace(/<p>:/ig, "<p> :"));
    }
    return messageBody.replace(/<a href=/g, "<a target=\"_blank\" class=\"open-new-window\" href=")
    .replace(/<a class="([a-z\s0-9]*)"\shref=/g, "<a target=\"_blank\" class=\"open-new-window\" href=");
  }

  static processMessageBody(message: Message): string {
    if (message.originalMessage) {
      return CommonUtil.processHTMLBody(message.originalMessage.replyMessage);
    }
    return CommonUtil.processHTMLBody(message.body);
  }

  static processHTMLBody(htmlBody) {
    if (!htmlBody) {
      return "";
    }
    return  htmlBody.replace("<body xmlns=\"http://www.w3.org/1999/xhtml\">", "")
    .replace("</body>", "")
    .replace(";lt;/body&amp;gt;", "")
    .replace(/&amp;nbsp;/ig, " ")
    .replace(/&lt;/ig, "<")
    .replace(/&gt;/ig, ">")
    .replace(/&quot;/ig, "\"")
    .replace(/&amp;lt;/ig, "&lt;")
    .replace(/&amp;gt;/ig, "&gt;")
    .replace(/&amp;amp;quote;/g, "&quote;")
    .replace(/&amp;amp;trade;/g, "&trade;")
    .replace(/&amp;amp;copy;/g, "&copy;")
    .replace(/&amp;amp;/ig, "&amp;");
  }

  static processSpecialChars(messageBody) {
    if (!messageBody) {
      return "";
    }
    return  messageBody
    .replace(/&amp;amp/g, "&amp;amp;amp")
    .replace(/&amp;lt/g, "&amp;amp;lt")
    .replace(/&amp;copy/g, "&amp;amp;copy")
    .replace(/&amp;trade/g, "&amp;amp;trade")
    .replace(/&amp;quote/g, "&amp;amp;quote")
    .replace(/&amp;gt/g, "&amp;amp;gt")
    .replace(/&amp;/g, "&");
  }

  static escapeEntities(text: string): string {
    if (!text) {
      return "";
    }
    return entities.escape(text);
  }

  static encodeHTML(text: string): string {
    if (!text) {
      return "";
    }
    return entities.encodeHTML(text);
  }

  static decodeHTML(text: string): string {
    if (!text) {
      return "";
    }
    return entities.decodeHTML(text);
  }

  static removeLeadingTrimmedBreaks(text: string): string {
    const trimmed = text.trim();
    // eslint-disable-next-line quotes
    if (trimmed.startsWith('<body xmlns="http://www.w3.org/1999/xhtml">')) {
      return CommonUtil.removeLeadingTrimmedBreaks(trimmed.substring(43));
    }
    // eslint-disable-next-line quotes
    if (trimmed.startsWith('.w3.org / 1999 / xhtml">')) {
      return CommonUtil.removeLeadingTrimmedBreaks(trimmed.substring(43));
    }
    if (trimmed.startsWith("<br />")) {
      return CommonUtil.removeLeadingTrimmedBreaks(trimmed.substring(6));
    }
    if (trimmed.startsWith("<br>")) {
      return CommonUtil.removeLeadingTrimmedBreaks(trimmed.substring(4));
    }
    if (trimmed.startsWith("</p>")) {
      return CommonUtil.removeLeadingTrimmedBreaks(trimmed.substring(4));
    }
    if (trimmed.startsWith("<p>")) {
      return CommonUtil.removeLeadingTrimmedBreaks(trimmed.substring(3));
    }

    return text;
  }

  static removeTrailingTrimmedBreaks(text: string): string {
    const trimmed = text.trim();
    if (trimmed.endsWith("</body>")) {
      return CommonUtil.removeTrailingTrimmedBreaks(trimmed.substring(0, trimmed.length - 7));
    }
    if (trimmed.endsWith("<br />")) {
      return CommonUtil.removeTrailingTrimmedBreaks(trimmed.substring(0, trimmed.length - 6));
    }
    if (trimmed.endsWith("<br>")) {
      return CommonUtil.removeTrailingTrimmedBreaks(trimmed.substring(0, trimmed.length - 4));
    }
    if (trimmed.endsWith("</p>")) {
      return CommonUtil.removeTrailingTrimmedBreaks(trimmed.substring(0, trimmed.length - 4));
    }
    if (trimmed.endsWith("<p>")) {
      return CommonUtil.removeTrailingTrimmedBreaks(trimmed.substring(0, trimmed.length - 3));
    }
    return text;
  }

  static processEmoji(messageBody) {
    if (!messageBody) {
      return "";
    }
    if (wdtEmojiBundle && wdtEmojiBundle.emoji) {
      wdtEmojiBundle.emoji.replace_mode = "unified";
      wdtEmojiBundle.emoji.allow_native = true;
    }
    return wdtEmojiBundle.render(messageBody.replace(/<br \/>/g, " ____<br />____ ")).replace(new RegExp(" ____<br />____ ", "g"), "<br />");
  }

  static beautifyName(text: string): string {
    if (!text) {
      return "";
    }
    return text.split(".").map(v => v.replace(/^\w/, c => c.toUpperCase())).join(" ");
  }

  static linkifyMe(inputText: string): string {
    if (!inputText) {
      return "";
    }
    const autolinker = new Autolinker({
      urls: {
        schemeMatches: true,
        wwwMatches: true,
        tldMatches: true
      },
      email: false,
      phone: false,
      mention: false,
      hashtag: false,

      stripPrefix: false,
      stripTrailingSlash: false,
      newWindow: true,

      truncate: {
        length: 0,
        location: "end"
      },

      className: "open-new-window"
    });

    return autolinker.link(inputText);
  }

  static linkify(inputText: string): string {
    if (!inputText) {
      return "";
    }

    let result = this.linkifyText2Html(inputText).replace(/<a href/ig, "<a class=\"open-new-window\" href");
    return result;
  }

  static linkifyText2Html(inputText: string) {
    // LoggerService.info("[CommonUtil][linkifyText2Html]", inputText);
    if (!inputText) {
      return "";
    }
    const isConfJoinLink = this.isOnIOS() && inputText.includes(ConstantsUtil.CONFERENCE_LINK_PART);
    // const isConfJoinLink = inputText.includes(ConstantsUtil.CONFERENCE_LINK_PART);
    let link = linkifyHtml(inputText, {
      defaultProtocol: "http",
      className: "open-new-window",
      target: {
        url: isConfJoinLink ? "_self" : "_blank",
        email: null
      },
      attributes: (href, type) => {
        if (type === "url") {
          const attrs = {rel: "noopener"};
          if (isConfJoinLink) {
            // https://stackoverflow.com/a/20548330/574475
            attrs["onclick"] = `const evv = new CustomEvent('${ConstantsUtil.CONFERENCE_LINK_CLICK_EVENT}', { 'detail': '${href}' }); document.dispatchEvent(evv);`;
          }
          return attrs;
        }
        return {};
      },
      formatHref: (href, type) => {
        if (type !== "email" && !isConfJoinLink) {
          return href;
        }
        return "javascript:void(0)";
      },
      tagName: (href, type) => {
        if (type !== "email") {
          return "a";
        }
        return "span";
      }
    });

    // LoggerService.info("[linkify]", inputText, link)
    if (link.indexOf(`<span href="javascript:void(0)" class="open-new-window">`) !== -1) {
      link = link.replace(new RegExp(`<span href="javascript:void\\(0\\)" class="open-new-window">`, "igm"), "")
      .replace("</span>", "");
    }
    return link;
  }


  ////

  static replaceName(type, content, query, name, isHighlighted?: boolean, keyword?: string, jid?:string): string {
    if (!content) {
      return "";
    }
    if (type === "plain") {
      content = content.replace(query, `@${name}`);
    } else {
      if (keyword) {
        name = CommonUtil.highlightSearch(name, keyword);
      }
      content = content.replace(query, `<span class="mentioned-user"  data-jid=${jid} >@${name}</span>`);

      if (isHighlighted) {
        this.highlightUser(content);
      }
    }
    return content;
  }

  static highlightUser(content){
    if (!content) {
      return "";
    }
    return content.replace(content, `<span class="mentioned-user">`, `<span class="mentioned-user highlighted">`);
  }

  static highlightSearch(text , keyword) {
    if (!text) {
      return "";
    }
    if (!keyword) {
      return text;
    }
    let newText = text;
    try {
      text = text.replace(/&#34;/g , "\"");
      if (keyword.includes("ä" || "ö" || "ü" || "ß" || "Ä" || "Ö" || "Ü" || "ẞ")) {
        keyword = this.convertToNcr(keyword);
      }
      if (keyword.includes(" ")) {
        keyword = keyword.split(" ").join("|");
      }
      newText = text;
      const query = new RegExp(this.toUnicode(keyword) , "gim");
      const doc = document.createElement("div");
      doc.innerHTML = newText;
      for (let dom of Array.from(doc.childNodes)) {
        if (dom.nodeValue) {
            dom.nodeValue = dom.nodeValue.replace(/(<span>|<\/span>)/igm , "").replace(query , "START_HIGHLIGHT$&END_HIGHLIGHT");
        } else if (dom.textContent) {
          dom.textContent = dom.textContent.replace(/(<span>|<\/span>)/igm , "").replace(query , "START_HIGHLIGHT$&END_HIGHLIGHT");
        }
      }
      // LoggerService.info("[highlightSearch]", doc.childNodes);
      newText = doc.innerHTML.replace(/START_HIGHLIGHT/g, "<span class=\"highlight\">").replace(/END_HIGHLIGHT/g, "</span>");
    } catch (error) {
      LoggerService.info("[highlightSearch] error", error);
    }
    return newText;
  }

  static getFileExtension(fileName: string): string {
    const fileType = (fileName.split(".").pop() || "").toLowerCase();
    return fileType;
  }

  static toUnicode(searchKeyword) {
    if (!searchKeyword) {
      return "";
    }
    let unicodeString = "";
    for (let i = 0; i < searchKeyword.length; i++) {
      let theUnicode = searchKeyword.charCodeAt(i).toString(16).toUpperCase();
      while (theUnicode.length < 4) {
        theUnicode = "0" + theUnicode;
      }
      theUnicode = "\\u" + theUnicode;
      unicodeString += theUnicode;
    }
    return unicodeString;
  }

  static convertToNcr(searchKeyword) {
    if (!searchKeyword) {
      return "";
    }
    let ncrKeyword = "";
    let singleChar;
    for (let i = 0; i < searchKeyword.length; i++) {
      singleChar = searchKeyword.charAt(i);
      if (this.decimalNcr[singleChar]) {
        ncrKeyword += this.decimalNcr[singleChar];
      }
      else {
        ncrKeyword += singleChar;
      }
    }
    return ncrKeyword;
  }


  ////


  static validateEmail(email) {
    // RFC822 version
    let sQtext = "[^\\x0d\\x22\\x5c\\x80-\\xff]";
    let sDtext = "[^\\x0d\\x5b-\\x5d\\x80-\\xff]";
    let sAtom = "[^\\x00-\\x20\\x22\\x28\\x29\\x2c\\x2e\\x3a-\\x3c\\x3e\\x40\\x5b-\\x5d\\x7f-\\xff]+";
    let sQuotedPair = "\\x5c[\\x00-\\x7f]";
    let sDomainLiteral = "\\x5b(" + sDtext + "|" + sQuotedPair + ")*\\x5d";
    let sQuotedString = "\\x22(" + sQtext + "|" + sQuotedPair + ")*\\x22";
    let sDomain_ref = sAtom;
    let sSubDomain = "(" + sDomain_ref + "|" + sDomainLiteral + ")";
    let sWord = "(" + sAtom + "|" + sQuotedString + ")";
    let sDomain = sSubDomain + "(\\x2e" + sSubDomain + ")*";
    let sLocalPart = sWord + "(\\x2e" + sWord + ")*";
    let sAddrSpec = sLocalPart + "\\x40" + sDomain; // complete RFC822 email address spec
    let sValidEmail = "^" + sAddrSpec + "$"; // as whole string

    let reValidEmail = new RegExp(sValidEmail);

    return reValidEmail.test(email);
  }

  static validatePhone(phone) {
    let reValidPhone = new RegExp("^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$");
    return reValidPhone.test(phone);
  }

  static logger(data: any) {
    LoggerService.info("Prashant Logger", data);
  }

  static isOnMobileDevice() {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|PlayBook/i
      .test(navigator.userAgent);
  }

  static isMobileSize() {
    return window.outerWidth < 768;
  }

  static isMobileSizeInnerWidth() {
    return window.innerWidth < 768;
  }

  static isIPadSize() {
    return window.outerWidth < 960;
  }

  static isOnIOSMobile() {
    return /iPhone|iPad|iPod/i
      .test(navigator.userAgent);
  }

  static isOnIpad() {
    return /iPad/i
      .test(navigator.userAgent.toLowerCase()) || (environment.isCordova && typeof device !== "undefined"
      && device.model && device.model.toLowerCase().indexOf("ipad") !== -1);
  }

  static isOnChromeBrowser() {
    return /chrome/.test(navigator.userAgent.toLowerCase());
  }

  static isOnChrome() {
    return this.isOnChromeBrowser() || environment.isElectron;
  }

  static isOnSafari() {
    return /safari/.test(navigator.userAgent.toLowerCase()) && !CommonUtil.isOnChrome();
  }

  static isOnSafariMobile() {
    return this.isOnSafari() && this.isOnIOSMobile();
  }

  static isOnFirefox() {
    return /firefox/.test(navigator.userAgent.toLowerCase());
  }

  static isOnEDGE() {
    return /edge/.test(navigator.userAgent.toLowerCase());
  }

  static isOnOpera() {
    return /opera/.test(navigator.userAgent.toLowerCase());
  }

  static isOnIOS() {
    return typeof device !== "undefined" && !!device.platform && device.platform.toUpperCase() === "IOS";
  }

  static isOnCordova() {
    return typeof cordova !== "undefined";
  }

  static isOnIOSXFamily() {
    // https://www.theiphonewiki.com/wiki/Models#iPhone
    const isXFamily = ["iPhone10,3", "iPhone10,6", "iPhone11,8", "iPhone11,2", "iPhone11,6"].includes(device.model);
    LoggerService.info("[CommonUtil][isOnIOSXFamily]", device.model, isXFamily);
    return CommonUtil.isOnIOS() && isXFamily;
  }

  static isOnAndroid() {
    return typeof device !== "undefined" && !!device.platform && device.platform.toUpperCase() === "ANDROID";
  }

  static isOnNativeMobileDevice() {
    return CommonUtil.isOnIOS() || CommonUtil.isOnAndroid();
  }

  static isOnNativeIPhone() {
    return CommonUtil.isOnIOS() && !CommonUtil.isOnIpad();
  }

  static isOnMacOS() {
    return navigator.appVersion.indexOf("Mac") !== -1;
    // if (navigator.appVersion.indexOf("Win")!=-1) OSName="Windows";
    // if (navigator.appVersion.indexOf("X11")!=-1) OSName="UNIX";
    // if (navigator.appVersion.indexOf("Linux")!=-1) OSName="Linux";
  }

  static getElectronOS() {
    let platform = window.navigator.platform,
        macosPlatforms = ["Macintosh", "MacIntel", "MacPPC", "Mac68K"],
        windowsPlatforms = ["Win32", "Win64", "Windows", "WinCE"],
        os = null;

    if (macosPlatforms.indexOf(platform) !== -1) {
      os = "macOS";
    } else if (windowsPlatforms.indexOf(platform) !== -1) {
      os = "Windows";
    } else if (!os && /Linux/.test(platform)) {
      os = "Linux";
    }

    return os;
  }

  static platformDescription() {
    let deviceName;
    if (environment.isCordova) {
      deviceName = device.model;

    // Electron / Web
    } else {
      deviceName = getBrowserFingerprint({ hardwareOnly: true, enableWebgl: true });
    }

    let platformName;
    if (environment.isCordova) {
      if (this.isOnIOS()) {
        platformName = `Mobile iOS`;
      } else {
        platformName =  `Mobile Android`;
      }
    } else if (environment.isElectron) {
      const os = this.getElectronOS();
      if (os) {
        platformName = `Desktop ${os}`;
      } else {
        platformName = "Desktop";
      }
    } else {
      if (this.isOnChromeBrowser()) {
        platformName = "Web Chrome";
      } else if (this.isOnSafari()) {
        platformName = "Web Safari";
      } else if (this.isOnFirefox()) {
        platformName = "Web Firefox";
      } else if (this.isOnEDGE()) {
        platformName = "Web EDGE";
      } else if (this.isOnOpera()) {
        platformName = "Web Opera";
      } else {
        platformName = "Web";
      }
    }

    const res = `${platformName}, ${deviceName}`;
    LoggerService.info("[CommonUtils][platformDescription]", res);

    return res;
  }

  static getDeviceId() {
    if (environment.isCordova) {
      return device.uuid;
    }
    return getBrowserFingerprint({ hardwareOnly: true, enableWebgl: true });
  }

  static isIndexedDBNotSupported() {
    return !("indexedDB" in window);
  }

  static isInsideIFrame() {
    try {
      return window.self !== window.top;
    } catch (e) {
      return true;
    }
  }

  static getBaseUrl() {
    if (environment.isCordova) {
      return CommonUtil.isOnAndroid() && window.location.href.startsWith("http") ? "" : window.location.href.split("/talk")[0].replace(/index.html/gi, "");
    } else if (environment.isElectron) {
      return window.location.href.split("/talk")[0].replace(/index.html/gi, "");
    } else {
      return "";
    }
  }

  static getFullUrl(url: string) {
    return CommonUtil.getBaseUrl() + url;
  }

  static trimString(subject: string, length: number, suffix: string = "...") {
    if (!subject) {
      return "";
    }
    if (subject.length <= length) {
      return subject;
    }

    const suffixLength = suffix.length;
    const trimLength = length - suffixLength;

    return subject.slice(0, trimLength - 1) + suffix;
  }

  static getMediaType(body: string): string {
    if (!body) {
      return MediaType.ATTACHMENTS;
    }
    if ((body.lastIndexOf("aac") === body.length - 3) ||
      (body.lastIndexOf("amr") === body.length - 3) ||
      (body.lastIndexOf("aiff") === body.length - 4) ||
      (body.lastIndexOf("flac") === body.length - 4) ||
      (body.lastIndexOf("mp3") === body.length - 3) ||
      (body.lastIndexOf("oog") === body.length - 3) ||
      (body.lastIndexOf("wma") === body.length - 3) ||
      (body.lastIndexOf("wav") === body.length - 3)) {
      return MediaType.VOICE_MESSAGE;
    }

    if ((body.lastIndexOf("jpeg") === body.length - 4) ||
      (body.lastIndexOf("jpg") === body.length - 3) ||
      (body.lastIndexOf("gif") === body.length - 3) ||
      (body.lastIndexOf("png") === body.length - 3) ||
      (body.lastIndexOf("bmp") === body.length - 3) ||
      (body.lastIndexOf("svg") === body.length - 3) ||
      (body.lastIndexOf("tiff") === body.length - 4) ||
      (body.lastIndexOf("ico") === body.length - 3) ||
      (body.lastIndexOf("odi") === body.length - 3)) {
      return MediaType.IMAGE;
    }

    if ((body.lastIndexOf("3gp") === body.length - 3) ||
      (body.lastIndexOf("avi") === body.length - 3) ||
      (body.lastIndexOf("flv") === body.length - 3) ||
      (body.lastIndexOf("m4v") === body.length - 3) ||
      (body.lastIndexOf("mkv") === body.length - 3) ||
      (body.lastIndexOf("mov") === body.length - 3) ||
      (body.lastIndexOf("mp4") === body.length - 3) ||
      (body.lastIndexOf("mpeg") === body.length - 4) ||
      (body.lastIndexOf("mpg") === body.length - 3) ||
      (body.lastIndexOf("mts") === body.length - 3) ||
      (body.lastIndexOf("rm") === body.length - 2) ||
      (body.lastIndexOf("vob") === body.length - 3) ||
      (body.lastIndexOf("wmv") === body.length - 3)) {
      return MediaType.VIDEOS;
    }

    if (body.lastIndexOf("pdf") === body.length - 3) {
      return MediaType.PDF;
    }

    if ((body.lastIndexOf("txt") === body.length - 3) ||
      (body.lastIndexOf("rtf") === body.length - 3) ||
      (body.lastIndexOf("dat") === body.length - 3)) {
      return MediaType.TEXT;
    }

    if ((body.lastIndexOf("doc") === body.length - 3) ||
      (body.lastIndexOf("docx") === body.length - 4) ||
      (body.lastIndexOf("docm") === body.length - 4) ||
      (body.lastIndexOf("odt") === body.length - 3)) {
      return MediaType.DOC;
    }

    if ((body.lastIndexOf("xls") === body.length - 3) ||
      (body.lastIndexOf("xlr") === body.length - 3) ||
      (body.lastIndexOf("xlsx") === body.length - 4) ||
      (body.lastIndexOf("xlsm") === body.length - 3) ||
      (body.lastIndexOf("ods") === body.length - 3) ||
      (body.lastIndexOf("csv") === body.length - 3) ||
      (body.lastIndexOf("tsv") === body.length - 3)) {
      return MediaType.EXCEL;
    }

    if ((body.lastIndexOf("ppt") === body.length - 3) ||
      (body.lastIndexOf("pptx") === body.length - 4) ||
      (body.lastIndexOf("pps") === body.length - 3) ||
      (body.lastIndexOf("odp") === body.length - 3)) {
      return MediaType.PPT;
    }

    if ((body.lastIndexOf("rar") === body.length - 3) ||
      (body.lastIndexOf("tar") === body.length - 3)) {
      return MediaType.RAR;
    }

    if ((body.lastIndexOf("zip") === body.length - 3) ||
      (body.lastIndexOf("7z") === body.length - 2)) {
      return MediaType.ZIP;
    }

    return MediaType.ATTACHMENTS;
  }

  static isKnownAttachmentMediaType(body: string): boolean {
    const fileShareService = !!localStorage.getItem("fileShareService") ? localStorage.getItem("fileShareService") : "https://phpfile.uxf.zimbra-vnc.de";
    return body.startsWith(fileShareService) || (this.getMediaType(body) !== MediaType.ATTACHMENTS);
  }

  static mapAddress(address: ContactAddress) {
    const availableKeys = ["street", "street2", "region", "city", "postalCode", "country", "addressType"];
    if (address.work) {
      address.addressType = "WORK";
    } else if (address.home) {
      address.addressType = "HOME";
    } else if (address.postal) {
      address.addressType = "POSTAL";
    }
    for (let key of Object.keys(address)) {
      if (availableKeys.indexOf(key) === -1) {
        delete address[key];
      }
    }
    for (let key of availableKeys) {
      if (!address[key]) {
        address[key] = "";
      }
    }
    return address;
  }


  static mapPhone(phone: TelInformation) {
    const availableKeys = ["number", "phoneType"];
    if (phone.work) {
      phone.phoneType = "WORK";
    } else if (phone.home) {
      phone.phoneType = "HOME";
    } else if (phone.fax) {
      phone.phoneType = "FAX";
    }
    for (let key of Object.keys(phone)) {
      if (availableKeys.indexOf(key) === -1) {
        delete phone[key];
      }
    }
    for (let key of availableKeys) {
      if (!phone[key]) {
        phone[key] = "";
      }
    }
    return phone;
  }

  static mapEmail(email: EmailInformation) {
    const availableKeys = ["email", "emailType"];
    if (email.work) {
      email.emailType = "WORK";
    } else if (email.home) {
      email.emailType = "HOME";
    } else if (email.internet) {
      email.emailType = "INTERNET";
    } else if (email.preferred) {
      email.emailType = "INTERNET";
    }
    for (let key of Object.keys(email)) {
      if (availableKeys.indexOf(key) === -1) {
        delete email[key];
      }
    }
    for (let key of availableKeys) {
      if (!email[key]) {
        email[key] = "";
      }
    }
    return email;
  }

  static isAudio(fileType: string): boolean {
    if (!fileType || (typeof fileType !== "string")) {
      return false;
    }
    return this.audioExtensions.indexOf(fileType?.toLowerCase()) !== -1;
  }

  static isSupportedVideo(fileType: string): boolean {
    if (!fileType || (typeof fileType !== "string")) {
      return false;
    }
    return this.supportedVideoExtensions.indexOf(fileType?.toLowerCase()) !== -1;
  }

  static isVCFfile(fileType: string): boolean {
    if (!fileType || (typeof fileType !== "string")) {
      return false;
    }
    return this.supportedVCFExtensions.indexOf(fileType?.toLowerCase()) !== -1;
  }

  static isImage(fileType: string): boolean {
    if (!fileType || (typeof fileType !== "string")) {
      return false;
    }
    return this.imagesExtensions.indexOf(fileType?.toLowerCase()) !== -1
    || ( fileType.toLowerCase().indexOf("image") !== -1 && fileType.toLowerCase().indexOf("appimage") === -1 );
  }

  static isVideo(fileType: string): boolean {
    if (!fileType || (typeof fileType !== "string")) {
      return false;
    }
    let type = fileType.split("/");
    if (type.length > 1 && type[1]) {
      fileType = type[1];
    }
    return this.videoExtensions.indexOf(fileType?.toLowerCase()) !== -1;
  }

  static isDocument(fileType: string): boolean {
    if (!fileType || (typeof fileType !== "string")) {
      return false;
    }
    return this.documentExtensions.indexOf(fileType?.toLowerCase()) !== -1;
  }

  static isValidEmail(email: string): boolean {
    if (email.endsWith(".")) {
      email = email.slice(0, email.length - 1);
    }
    return EMAIL_REGEXP.test(email);
  }

  static getMediaUrl(type: string): string {
    let url = "";

    if (type === MediaType.VOICE_MESSAGE) {
      url = "/assets/media-icons/file-document-mic.svg";
    }
    if (type === MediaType.VIDEOS) {
      url = "/assets/media-icons/file-document-video.svg";
    }
    if (type === MediaType.PDF) {
      url = "/assets/media-icons/file-pdf-box.svg";
    }
    if (type === MediaType.DOC) {
      url = "/assets/media-icons/icons8-microsoft-word.svg";
    }
    if (type === MediaType.RAR || type === MediaType.ZIP) {
      url = "/assets/media-icons/zip-box.svg";
    }
    if (type === MediaType.EXCEL) {
      url = "/assets/media-icons/icons8-microsoft-excel.svg";
    }
    if (type === MediaType.TEXT) {
      url = "/assets/media-icons/file-document-box.svg";
    }
    if (type === MediaType.PPT) {
      url = "/assets/media-icons/icons8-microsoft-powerpoint.svg";
    }
    if (type === MediaType.ATTACHMENTS) {
      url = "/assets/media-icons/file-document-box.svg";
    }

    return this.getFullUrl(url);
  }

  static hideBackdrop() {
    // mdl-backdrop-overlay
    if (document.querySelector("mdl-backdrop-overlay") !== null) {
      (<HTMLElement>document.querySelector("mdl-backdrop-overlay")).style.display = "none";
    }
    if (CommonUtil.isOnMobileDevice() && document.querySelector(".dialog-backdrop") !== null) {
      (<HTMLElement>document.querySelector(".dialog-backdrop")).style.display = "none";
    }
  }

  static showBackdrop() {
    // mdl-backdrop-overlay
    if (document.querySelector("mdl-backdrop-overlay") !== null) {
      (<HTMLElement>document.querySelector("mdl-backdrop-overlay")).style.display = "block";
    }
    if (CommonUtil.isOnMobileDevice() && document.querySelector(".dialog-backdrop") !== null) {
      (<HTMLElement>document.querySelector(".dialog-backdrop")).style.display = "block";
    }
  }

static isToday(date: Date) {
  const today = startOfDay(new Date());
  return isSameDay(date, today);
}

static isYesterday(date: Date) {
  const yesterday = subDays(startOfDay(new Date()), 1);
  return isSameDay(date, yesterday);
}

static lastActivity(seconds: number): { key: string, value: number | string } {
  const lastTime = subSeconds(new Date(), seconds);
  return this.timeAgo(lastTime.getTime());
}

static getLastTime(seconds: number, prefix: string): { key: string, value: number | string } {
  const lastTime = subSeconds(new Date(), seconds);
  return this.timeJoinedAgo(lastTime.getTime(), prefix);
}

static timeAgo (timestamp: number, DWMY_timeAgo = true) { // DWMY_timeAgo = [Days,Weeks,Months,Years] ago
  return CommonUtil.timeAgoPrefix(timestamp, DWMY_timeAgo, "LAST_SEEN");
}

static timeJoinedAgo (timestamp: number, prefix = "ONLINE") { // DWMY_timeAgo = [Days,Weeks,Months,Years] ago
  return CommonUtil.timeAgoPrefix(timestamp, true, prefix);
}

static timeAgoPrefix (timestamp: number, DWMY_timeAgo = true, prefix: string) { // DWMY_timeAgo = [Days,Weeks,Months,Years] ago
  const date = new Date(timestamp); // Getting date and time with unix timestamp
  const NOW = new Date();
  const dateTime    = {
        seconds        : differenceInSeconds(NOW, date),
        minutes        : differenceInMinutes(NOW, date),
        hours          : differenceInHours(NOW, date),
        days           : differenceInDays(NOW, date),
        weeks          : differenceInWeeks(NOW, date),
        months         : differenceInMonths(NOW, date),
        years          : differenceInYears(NOW, date),
        today          : this.isToday(date),
        yesterday      : this.isYesterday(date),
        dayName        : format(date, "cccc"),
        fullDateTime   : format(date, "PPpp"),
        date           : format(date, "PP"),
        time           : format(date, "p")
      };
    const datetime = dateTime.date + " " + dateTime.time;
    let outputTime: {
      key: string,
      value: number | string
    };


    if (dateTime.seconds > 0) {
      outputTime = {key: prefix + "_SECOND", value: dateTime.seconds};
    }
    if (dateTime.seconds > 1) {
      outputTime =  {key: prefix + "_SECOND", value: dateTime.seconds};
    }

    if (dateTime.minutes === 1) {
      outputTime = {key: prefix + "_MINUTE", value: dateTime.minutes};
    }
    if (dateTime.minutes > 1) {
      outputTime = {key: prefix + "_MINUTES", value: dateTime.minutes};
    }

    if (dateTime.hours === 1) {
      outputTime = {key: prefix + "_HOUR", value: dateTime.hours};
    }
    if (dateTime.hours > 1) {
      outputTime = {key: prefix + "_HOURS", value: dateTime.hours};
    }

    if (dateTime.days === 1) {
      if (DWMY_timeAgo) {
        outputTime = {key: prefix + "_DAY", value: dateTime.days};
      } else {
        outputTime = {key: prefix + "_DEFAULT", value: datetime};
      }
    }
    if (dateTime.days > 1) {
      if (DWMY_timeAgo) {
        outputTime = {key: prefix + "_DAYS", value: dateTime.days};
      } else {
        outputTime = {key: prefix + "_DEFAULT", value: datetime};
      }
    }

    if (dateTime.weeks === 1) {
      if (DWMY_timeAgo) {
        outputTime = {key: prefix + "_WEEK", value: dateTime.weeks};
      } else {
        outputTime = {key: prefix + "_DEFAULT", value: datetime};
      }
    }
    if (dateTime.weeks > 1) {
      if (DWMY_timeAgo) {
        outputTime = {key: prefix + "_WEEKS", value: dateTime.weeks};
      } else {
        outputTime = {key: prefix + "_DEFAULT", value: datetime};
      }
    }

    if (dateTime.months === 1) {
      if (DWMY_timeAgo) {
        outputTime = {key: prefix + "_MONTH", value: dateTime.months};
      } else {
        outputTime = {key: prefix + "_DEFAULT", value: datetime};
      }
    }
    if (dateTime.months > 1) {
      if (DWMY_timeAgo) {
        outputTime = {key: prefix + "_MONTHS", value: dateTime.months};
      } else {
        outputTime = {key: prefix + "_DEFAULT", value: datetime};
      }
    }

    if (dateTime.years === 1) {
      if (DWMY_timeAgo) {
        outputTime = {key: prefix + "_YEAR", value: dateTime.years};
      } else {
        outputTime = {key: prefix + "_DEFAULT", value: datetime};
      }
    }
    if (dateTime.years > 1) {
      if (DWMY_timeAgo) {
        outputTime = {key: prefix + "_YEARS", value: dateTime.years};
      } else {
        outputTime = {key: prefix + "_DEFAULT", value: datetime};
      }
    }

    if (dateTime.yesterday) {
      outputTime = {key: prefix + "_DAY", value: 1};
    }

    return outputTime;
  }

  static renderEmoji(text: string): string {
    if (!text) {
      return "";
    }
    wdtEmojiBundle.emoji.replace_mode = "unified";
    wdtEmojiBundle.emoji.allow_native = true;
    const result = wdtEmojiBundle.emoji.replace_emoticons(text.replace(/<br \/>/g, " ____<br />____ "));
    return result.replace(new RegExp(" ____<br />____ ", "g"), "<br />");
  }

  static renderBodyWithEmoji(content: string, translateService: TranslateService): string {
    content = this.renderEmoji(content);
    if (content.trim().startsWith("http") || content.trim().startsWith("https")) {
      const link = content.trim().toLocaleLowerCase();
      content = this.renderEmoji(":link: ") + translateService.instant("LINK");
      const extension = link.split(".").pop();
      if (extension && this.imagesExtensions.indexOf(extension) !== -1) {
        content = this.renderEmoji(":camera: ") + translateService.instant("PHOTO");
      } else if (extension && this.audioExtensions.indexOf(extension) !== -1) {
        content = this.renderEmoji(":studio_microphone: ") + translateService.instant("AUDIO");
      } else if (extension && this.videoExtensions.indexOf(extension) !== -1) {
        content = this.renderEmoji(":film_frames: ") + translateService.instant("VIDEO_EXTENSION");
      }
    }

    return content;
  }

  static uInt8ArrayToString(uint8): string {
    return new TextDecoder("utf-8").decode(uint8);
  }

  static getDefaultLang(): string {
    let localLang = localStorage.getItem("portalLanguage");
    if (localLang === null) {
      localLang = navigator.language.split("-")[0];
    }
    const lang = localLang.match(/en|de|fr|ru/) ? localLang : "en";
    localStorage.setItem("portalLanguage", lang);
    return lang;
  }

  static isBroadcast(target: string): boolean {
    if (!target) {
      return false;
    }
    return target.startsWith("broadcast@") || target.startsWith("broadcast-");
  }

  static removeNonprintableUnicodeCharacters(originString: string): string {
    if (!originString) {
      return "";
    }
    return originString.replace(NON_PRINTABLE_UNICODE_CHARACTERS, "");
  }

  static destroyQRScanCode(): void {
    if (environment.isCordova) {
      document.getElementById("hideCamera").setAttribute("style", "display: none");
      document.querySelector("body").classList.remove("show-camera");
      document.getElementsByTagName("vp-app")[0].removeAttribute("style");
      localStorage.removeItem("scanQRCode");
      QRScanner.destroy();
    }
  }

  static getContactEmailByContactJid(jidOrEmail, contact) {
    const email = (contact &&
                  contact.emails &&
                  contact.emails[0] &&
                  contact.emails[0].email !== jidOrEmail) ? contact.emails[0].email : jidOrEmail;

    return email;
  }

  static parseMentions(text: string): string[] {
    if (!text) {
      return [];
    }
    const mentionsRegex = new RegExp("@([a-zA-Z0-9\_\.\-]+@[a-zA-Z0-9\_\.\-]+)", "gim");
    let matches = text.match(mentionsRegex);
    if (matches && matches.length) {
        matches = matches.map((match) => {
            return match.slice(1);
        });
        return CommonUtil.uniq(matches.filter(v => CommonUtil.isValidEmail(v)));
    } else {
        return [];
    }
  }

  static parseTicketMentions(prefix: string, text: string): string[] {
    if (!text) {
      return [];
    }
    const mentionsRegex = new RegExp(prefix + "#([a-zA-Z0-9\_\.\-]+@[a-zA-Z0-9\_\.\-]+)", "gim");
    LoggerService.info("[parseMentions]", mentionsRegex);
    let matches = text.match(mentionsRegex);
    if (matches && matches.length) {
        return matches.filter(v => CommonUtil.isValidEmail(v));
    } else {
        return [];
    }
  }

  static getMembers(text: string): string[] {
    if (!text) {
      return [];
    }
    const mentionsRegex = new RegExp("@([a-zA-Z0-9\_\.]+@[a-zA-Z0-9\_\.\-]+)", "gim");
    let matches = text.match(mentionsRegex);
    if (matches && matches.length) {
        matches = matches.map((match) => {
            return match.slice(1);
        });
        return CommonUtil.uniq(matches);
    } else {
        return [];
    }
  }

  static getMemberNames(text: string): string[] {
    if (!text) {
      return [];
    }
    return text.match(/([@][a-zA-Z0-9!@#\$%\^\&*\)\(+=._-]+)/g);
  }

  static getIcon(appName: string): string {
    let icon = "";
    let name = appName.replace("vnc", "VNC");
    switch (appName.toLowerCase()) {
      case "vncmail": icon = CommonUtil.getFullUrl("/assets/svg/vnc-products/vncmail.svg"); break;
      case "vnctask": icon = CommonUtil.getFullUrl("/assets/svg/vnc-products/vnctask.svg"); break;
      case "vncmcb": icon = CommonUtil.getFullUrl("/assets/svg/vnc-products/vncmcb.svg"); break;
      case "vnccontacts": icon = CommonUtil.getFullUrl("/assets/svg/vnc-products/vnccontacts.svg"); break;
      case "vnctalk": icon = CommonUtil.getFullUrl("/assets/svg/vnc-products/vnctalk.svg"); break;
      case "vnccalendar": icon = CommonUtil.getFullUrl("/assets/svg/vnc-products/vnccalendar.svg"); break;
      case "vncproject": icon = CommonUtil.getFullUrl("/assets/svg/vnc-products/vncproject.svg"); break;
      default: icon = CommonUtil.getFullUrl("/assets/icons/" + name + ".svg"); break;
    }
    return icon;
  }

  static getPackageName(appName: string): string {
    let packageName = `biz.vnc.${appName.toLowerCase()}`;
    if (appName.toLowerCase() === "vnccalendar") {
      packageName = `biz.vnc.vncmail`;
    }
    return packageName;
  }

  static getAppUrl(appName: string): string {
    const availableiOSApps = {
      "vncmail": "",
      "vnctask": "itms-apps://itunes.apple.com/app/id1423089930",
      "vnccontacts": ""
    };
    const availableAndroidApps = {
      "vncmail": "",
      "vnctask": "market://details?id=biz.vnc.vnctask",
      "vnccontacts": ""
    };
    if (!CommonUtil.isOnAndroid()) {
      return availableiOSApps[appName.toLowerCase()];
    }
    return availableAndroidApps[appName.toLowerCase()];
  }

  static getAppStoreOrWeblink(appItem: any) {
    let appLink = appItem.path;
    if (CommonUtil.isOnAndroid()) {
      switch (appItem.name.toLowerCase()) {
        case "vncmail": appLink = "https://play.google.com/store/apps/details?id=biz.vnc.vncmail"; break;
        case "vnccalendar": appLink = "https://play.google.com/store/apps/details?id=biz.vnc.vncmail"; break;
        case "vnccontacts": appLink = "https://play.google.com/store/apps/details?id=biz.vnc.contactsplus"; break;
        case "vnctalk": appLink = "https://play.google.com/store/apps/details?id=biz.vnc.vnctalk"; break;
        case "vnctask": appLink = "https://play.google.com/store/apps/details?id=biz.vnc.vnctask"; break;
        default: appLink = appItem.path;
      }
    } else if (CommonUtil.isOnIOS()) {
      switch (appItem.name.toLowerCase()) {
        case "vncmail": appLink = "https://apps.apple.com/us/app/vncmail-email-communication/id1448862526"; break;
        case "vnccalendar": appLink = "https://apps.apple.com/us/app/vncmail-email-communication/id1448862526"; break;
        case "vnccontacts": appLink = "https://apps.apple.com/us/app/vnccontacts/id1536718018"; break;
        case "vnctalk": appLink = "https://apps.apple.com/us/app/vnctalk/id1400937435"; break;
        case "vnctask": appLink = "https://apps.apple.com/us/app/vnctask/id1423089930"; break;
        default: appLink = appItem.path;
      }
    }
    return appLink;
  }

  static getAppSettings(settings: AppSettings): AppSettings {
    const defaultSettings: AppSettings = {
      lang: CommonUtil.getDefaultLang(),
      receipts: true,
      informTyping: true,
      publishProfile: true,
      publicLastActivity: true,
      addTimestamp: false,
      enableOperators: true,
      globalMute: false,
      hideInsecureContent: true,
      useEncryption: false,
      encryptionType: 0,
      top_p: 1.0,
      top_k: 50,
      temperature: 1.0,
      max_tokens: 200,
      vibration: true,
      ledNotification: true,
      enableSound: true,
      notificationSound: "0",
      silentHours: true,
      daysOptions: "EVERY_DAY",
      startTime: "00:00",
      endTime: "23:30",
      sendKey: "Enter",
      sendMessageWithEnter: true,
      enableAutofocus: false,
      enableChannels: true,
      enableSocials: true,
      enableBroadcasts: true,
      broadcastShowBadge: true,
      mentionShowBadge: true,
      enableWhiteboard: true,
      enableMCB: true,
      enablePad: true,
      use2FA: false,
      isAndroidAppUpgradeEnabled: true,
      enableOmemoEncryption: true,
      allowScreensharePreviewNew: true,
      enableRTF: true,
      leftSwipe: false,
      rightSwipe: false,
      leftAction: "VOICE_CALL",
      rightAction: "DELETE",
      enableGlobalMute: false,
      videoMinimalSaved: false,
      vncSafeUploadFolder: "/vnctalk uploads",
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
    };

    const booleanKeys = [
      "receipts",
      "informTyping",
      "publishProfile",
      "publicLastActivity",
      "allowScreensharePreviewNew",
      "addTimestamp",
      "enableOperators",
      "globalMute",
      "useEncryption",
      "vibration",
      "ledNotification",
      "enableSound",
      "silentHours",
      "enableAutofocus",
      "hideInsecureContent",
      "enableRTF",
      "enableChannels",
      "enableSocials",
      "enableBroadcasts",
      "broadcastShowBadge",
      "mentionShowBadge",
      "enableWhiteboard",
      "enableMCB",
      "enablePad",
      "use2FA",
      "isAndroidAppUpgradeEnabled",
      "enableOmemoEncryption",
      "enableGlobalMute",
      "leftSwipe",
      "rightSwipe"
    ];

    let nonBooleanValues = {};
    let booleanValues = {};

    Object.keys(settings).filter(k => booleanKeys.indexOf(k) === -1).map(v => {
      nonBooleanValues = {
        ...nonBooleanValues,
        [v]: settings[v]
      };
    });

    Object.keys(settings).filter(k => booleanKeys.indexOf(k) !== -1).map(v => {
      booleanValues = {
        ...booleanValues,
        [v]: !!settings[v]
      };
    });

    LoggerService.info("[CommonUtils][appSettings] booleanValues: ", booleanValues);
    return {
      ...defaultSettings,
      ...nonBooleanValues,
      ...booleanValues
    };
  }


  static getMediaDevices(): Observable<any> {
    LoggerService.info("[CommonUtils][getMediaDevices]");
    if (navigator.mediaDevices && navigator.mediaDevices.enumerateDevices) {
      return new Observable((observer: Observer<any>) => {
        navigator.mediaDevices.enumerateDevices().then((devices) => {

          // // test when there is no cam connected
          // devices = devices.filter(d => d.kind !== "videoinput");

          LoggerService.info("[CommonUtils][getMediaDevices]", devices);

          observer.next(devices);
          observer.complete();
        });
      });
    } else if (!(navigator.mediaDevices && navigator.mediaDevices.enumerateDevices) && window.MediaStreamTrack
      && window.MediaStreamTrack.getSources) {
      let enumerateDevices = window.MediaStreamTrack.getSources
        .bind(window.MediaStreamTrack);
      return new Observable((observer: Observer<any>) => {
        enumerateDevices((devices) => {
          LoggerService.info("[CommonUtils][getMediaDevices]", devices);
          observer.next(devices);
          observer.complete();
        });
      });
    }

    return from([]);
  }

  static removeMessageFromPending(mId: string): any {
    LoggerService.info("[CommonUtil][removeMessageFromPending]", mId);

    const pendingMessages = localStorage.getItem("pendingMessages");
    let msgs = [];
    if (pendingMessages) {
      msgs = JSON.parse(pendingMessages);
      if (msgs.length === 0) {
        return msgs;
      }
      msgs = msgs.filter(message => message.id !== mId);
    }
    localStorage.setItem("pendingMessages", JSON.stringify(msgs));
    return msgs;
  }

  static removeMessagesFromPending(mIds: string[]): any {
    LoggerService.info("[CommonUtil][removeMessagesFromPending]", mIds);

    const pendingMessages = localStorage.getItem("pendingMessages");
    let msgs = [];
    if (pendingMessages) {
      msgs = JSON.parse(pendingMessages);
      if (msgs.length === 0) {
        return msgs;
      }
      msgs = msgs.filter(message => !mIds.includes(message.id));
    }
    localStorage.setItem("pendingMessages", JSON.stringify(msgs));
    return msgs;

  }

  static textNodesUnder(root){
    let walk = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null), text = [], node;
    while ( node = walk.nextNode()) {
      text.push(node);
    }
    walk = null;
    return text;
  }

  static isGroupTarged(target) {
    return target && target.indexOf("@conference") !== -1;
  }

  static highlightWords(n, word) {
    if (!!n) {
      let after = document.createElement("span");
      for (let i; (i = n.nodeValue.indexOf(word, i)) > -1; n = after){
        after = n.splitText(i + word.length);
        let highlighted = n.splitText(i);
        let span = document.createElement("span");
        span.className = "highlight";
        span.appendChild(highlighted);
        after.parentNode.insertBefore(span, after);
        after = null;
        span = null;
      }
    }
  }

  static highlightWord(text, word): string {
    const parser = new DOMParser();
    const doc = parser.parseFromString(text, "text/html");
    const nodes = CommonUtil.textNodesUnder(doc);
    nodes.forEach(node => {
      CommonUtil.highlightWords(node, word);
    });
    return doc.querySelector("body").innerHTML;
  }

  static sentryLog(text: string) {
    if (!!localStorage.getItem("userJID")) {
      const jid = localStorage.getItem("userJID");
      text = `[JID]${jid} - [MESSAGE] ${text}`;
    }
    LoggerService.info("[sentryLog]", text);
    if (environment.enableSentry && environment.production) {
      try {
        Sentry.captureMessage(text);
      } catch (ex) {
        LoggerService.error(ex);
      }
    }
  }

  static getTargetForOneToOne(ownTarget: string, currentTarget: string): string {
    const participants: string[] = [ownTarget, currentTarget];
    participants.sort((a, b) => {
      if (a > b) return 1;
      return -1;
    });
    const target = participants.join("#").toLowerCase();

    return target;
  }

  static md5(text: string) {
    const md5 = new Md5();
    return md5.appendStr(text).end().toString();
  }

  static getMobileDeviceLabel() {
    if ((typeof device !== "undefined") && ((device.platform.toUpperCase() === "ANDROID") || (device.platform.toUpperCase() === "IOS"))) {
      return device.manufacturer + " " + device.model;
    } else {
      return "";
    }
  }

  static openLocalFile(localFileUrl): Observable<string> {
    const subject = new Subject<string>();
    let mimeType = CommonUtil.getMediaType(localFileUrl);
    LoggerService.info("[MessageComponent][openLocalFile] localFileUrl: " + localFileUrl + ", mimeType: " + mimeType);

    if (CommonUtil.isOnAndroid()) {
      // this plugin is only for Android
      cordova.plugins.FileOpener.canOpenFile(localFileUrl, () => {
        cordova.plugins.FileOpener.openFile(localFileUrl, () => {
          LoggerService.info("[MessageComponent][openLocalFile] success");
          subject.next("FILE_DOWNLOADED");
        }, (error) => {
          LoggerService.error("[MessageComponent][openLocalFile] openFile error", error);
          subject.next("SOME_UNKNOWN_ERROR");
        });
      }, (error) => {
        LoggerService.error("[MessageComponent][downloadFileAttachment] canOpenFile error", error);
      });

    // iOS
    } else {
      if (CommonUtil.isOnIpad()) {
        mimeType = CommonUtil.getMimeType(localFileUrl);
        LoggerService.info("[MessageComponent][downloadFileAttachment] showOpenWithDialog", localFileUrl, mimeType);
        cordova.plugins.fileOpener2.open(
          localFileUrl,
          mimeType,
          {
              error : (e)  => {
                  LoggerService.error("[MessageComponent][downloadFileAttachment] Error status: " + e.status + " - Error message: " + e.message);
              },
              success : () => {
                  LoggerService.info("[MessageComponent][downloadFileAttachment] file opened successfully");
              }
          }
      );
      } else {
        cordova.plugins.fileOpener2.showOpenWithDialog(
          localFileUrl,
          mimeType,
          {
              error : (e)  => {
                  LoggerService.error("[MessageComponent][downloadFileAttachment] Error status: " + e.status + " - Error message: " + e.message);
              },
              success : () => {
                  LoggerService.info("[MessageComponent][downloadFileAttachment] file opened successfully");
              }
          }
        );
      }
    }
    return subject.asObservable().pipe(take(1));
  }

  static HTML2Plaintext(html: string): string {
    if (!html) {
      return "";
    }
    return toPlaintext(html);
  }

  static HTMLToPlainText(str: string): string {
    if (str !== undefined && str !== null) {
        return str.replace(/<\/div>/ig, "\n").replace(/<\/p>/ig, "\n").replace(/<[^>]+>/ig, "");
    } else {
        return "";
    }
  }

  static html2text(html) {
    const tag = document.createElement("div");
    tag.innerHTML = html;
    return tag.innerText;
  }

  static getInnerText(el) {
    let sel, range, innerText = "";
    if (typeof document.selection !== "undefined" && typeof document.body.createTextRange !== "undefined") {
        range = document.body.createTextRange();
        range.moveToElementText(el);
        innerText = range.text;
    } else if (typeof window.getSelection !== "undefined" && typeof document.createRange !== "undefined") {
        sel = window.getSelection();
        sel.selectAllChildren(el);
        innerText = "" + sel;
        sel.removeAllRanges();
    }
    return innerText;
}

  static getMimeType(extension): string {
    const mType = mime.lookup(extension);
    if (typeof mType === "string") {
      return mType;
    }
    return "unknown";
  }

  static addInvitedEmail(email: string) {
    let mailList = [];
    if (!!localStorage.getItem("invitedMail")) {
      mailList = JSON.parse(localStorage.getItem("invitedMail"));
    }
    if (mailList.indexOf(email) === -1) {
      mailList.push(email);
    }
    localStorage.setItem("invitedMail", JSON.stringify(mailList));
  }

  static removeInvitedEmail(email: string) {
    let mailList = [];
    if (!!localStorage.getItem("invitedMail")) {
      mailList = JSON.parse(localStorage.getItem("invitedMail"));
      mailList = mailList.filter(v => v !== email);
      localStorage.setItem("invitedMail", JSON.stringify(mailList));
    }
  }

  static isInvitedEmail(email: string) {
    let mailList = [];
    if (!!localStorage.getItem("invitedMail")) {
      mailList = JSON.parse(localStorage.getItem("invitedMail"));
    }
    return mailList.indexOf(email) !== -1;
  }

  static convertDisplayNameToEmail(target) {
    if (!target) {
      return "";
    }
    return target.replace("_at_", "@").replace(/_dot_/g, ".").toLowerCase();
  }

  static isVideoMeeting(target: string): boolean {
    return target && target.indexOf("_meeting@conference.") !== -1 && target.indexOf("__meeting@conference.") === -1;
  }

  static isQuickMeeting(target: string): boolean {
    return target && target.indexOf("quick_meeting@conference.") !== -1;
  }

  static isTemporaryGroup(target: string): boolean {
    return target && target.indexOf("_temporary_group@conference.") !== -1;
  }

  static signJWT(email: string, roomId: string, target?:string, password?: string, jitsihost?: string) {
    const jwtObject = {
      email,
      roomId,
      target,
      password,
      jitsihost
    };
    return jwt.sign( jwtObject, "none");
  }

  static signJWTMCB(email: string, roomId: string, target: string, mcbId: number, password?: string) {
    const jwtObject = {
      email,
      roomId,
      target,
      mcbId,
      password
    };
    return jwt.sign(jwtObject, "none");
  }

  static verifyJWT(token: string): any {
    return jwt.verify( token, "none");
  }

  static decodeJWT(token: string): any {
    return jwt.decode(token);
  }


  static getCallUrl(email: string, roomId: string, password?: string): string {
    const jwt = CommonUtil.signJWT(email, roomId, password);
    let serverURL = window.location.origin;
    if (environment.isCordova || environment.isElectron) {
        serverURL = localStorage.getItem("serverURL");
    }
    const callUrl = `${serverURL}${ConstantsUtil.CONFERENCE_LINK_PART}${jwt}`;
    return callUrl;
  }

  static getCallUrlForInvitation(email: string, roomId: string, target?: string, password?: string): string {
    let jitsihost = localStorage.getItem("currentJitsiHost");
    const jwt = !!jitsihost ? CommonUtil.signJWT(email, roomId, target, password, jitsihost) : CommonUtil.signJWT(email, roomId, target, password);
    let serverURL = window.location.origin;
    if (environment.isCordova || environment.isElectron) {
        serverURL = localStorage.getItem("serverURL");
    }
    const callUrl = `${serverURL}${ConstantsUtil.CONFERENCE_ASSETS_LINK_PART}?jwt=${jwt}`;
    return callUrl;
  }

  static getConnectionQuality(connectionStrength): string {
    if (connectionStrength === null || connectionStrength === undefined) {
      return CommonUtil.getDefaultConnectionQuality();
    }

    // On mobile application, we are not able to access bandwidthRange from config, so putting it here.
    const bandwidthRange = {
      good: { from: 80, to: 100 },   // 24 - 30
      average: { from: 50, to: 79 }, // 15 - 24
      poor: { from: 0, to: 49 }      // 0 - 15
    };

    if (bandwidthRange.good.from <= connectionStrength) {
      return ConstantsUtil.USER_BANDWIDTH_QUALITY.good;
    } else if (bandwidthRange.average.from <= connectionStrength) {
      return ConstantsUtil.USER_BANDWIDTH_QUALITY.average;
    } else {
      return ConstantsUtil.USER_BANDWIDTH_QUALITY.poor;
    }
  }

  static getDefaultConnectionQuality(): string {
    return ConstantsUtil.USER_BANDWIDTH_QUALITY.good;
  }

  static getCallParticipantStatsString(stats: CallParticipantStats): string {
    return `
      <strong><span class="stats-label">Connection:</span> <span> ${stats.connectionQuality}</span></strong><br>
      <span class="stats-label">Online time:</span> <span>${stats.onlineTime || "NA"}</span><br>
      <span class="stats-label">Last speak::</span> <span> ${stats.laskSpeak || "NA"}</span><br>
      <span class="stats-label">Bitrate::</span> <span> ${stats.bitrate}</span><br>
      <span class="stats-label">Packet loss::</span> <span> ${stats.packetLoss}</span><br>
      <span class="stats-label">Resolution::</span> <span> ${stats.resolution}</span><br>
      <span class="stats-label">Frame rate::</span> <span> ${stats.framerate}</span><br>
    `;
  }

  static encrypt(plainText) {
    if (!plainText) {
      return "";
    }
    return simpleCrypto.encrypt(plainText);
  }

  static decrypt(plainText) {
    if (!plainText) {
      return "";
    }
    return simpleCrypto.decrypt(plainText);
  }

  static limitContent(text: string, limit): string {
    if (!text) {
      return "";
    }
    if (text.length > limit) {
      for (let i = limit; i > 0; i--) {
        if (text.charAt(i) === " " && (text.charAt(i - 1) !== "," || text.charAt(i - 1) !== "." || text.charAt(i - 1) !== ";")) {
          return text.substring(0, i) + "...";
        }
      }
      return text.substring(0, limit) + "...";
    } else {
      return text;
    }
  }

  static onOmemoLibsignalLoaded(): Observable<any> {
    LoggerService.info("[CommonUtil][onOmemoLibsignalLoaded]");
    const response = new BehaviorSubject<number>(0);
    const checker = () => {
      if (!window.libsignal) {
        LoggerService.info("[CommonUtil][onOmemoLibsignalLoaded] !window.libsignal");
        setTimeout(() => {
          LoggerService.info("[CommonUtil][onOmemoLibsignalLoaded] after timeout");
          response.next(0);
          checker();
        }, 300);
      } else {
        LoggerService.info("[CommonUtil][onOmemoLibsignalLoaded] libsignal inited");
        response.next(1);
        response.complete();
      }
    };
    checker();
    return response.asObservable();
  }

  static beautifyMailContent(content: string): string {
    if (!content) {
      return "";
    }
    const parser = new DOMParser();
    const htmlDoc = parser.parseFromString(content, "text/html");
    htmlDoc.body.innerHTML = CommonUtil.linkify(htmlDoc.body.innerHTML);
    for (let element of Array.from(htmlDoc.querySelectorAll("style"))) {
      const dom = <HTMLElement>element;
      dom.remove();
    }
    for (let element of Array.from(htmlDoc.querySelectorAll("meta"))) {
      const dom = <HTMLElement>element;
      dom.remove();
    }
    for (let element of Array.from(htmlDoc.querySelectorAll("table"))) {
      const dom = <HTMLElement>element;
      dom.style.width = "auto";
      dom.style.maxWidth = "auto";
      dom.removeAttribute("width");
    }
    if (!!htmlDoc.body.getAttribute("style")) {
      const result = htmlDoc.body.innerHTML;
      const style = htmlDoc.body.getAttribute("style").replace(/\"/g, "'");
      htmlDoc.body.innerHTML = `<div class="content-wrapper" style="${style}">${result}</div>`;
    }
    return htmlDoc.body.innerHTML;
  }

  static replaceLinkToAnchor(content: string): string {
    if (content === "" || content === undefined) {
      return "";
    }
    return content.replace(/(?:https?\:\/\/|www\.)+(?![^\s]*?")([\w.,@?!^=%&amp;:()\/~+#-]*[\w@?!^=%&amp;()\/~+#-])?/ig, (url) => {
      const wrap = document.createElement("div");
      const anchor = document.createElement("a");
      let href = url;
      if (!url.startsWith("http") && !url.startsWith("https")) {
        href = "http://" + url;
      }
      anchor.href = href.replace(/&#64;/g, "@");
      anchor.target = "_blank";
      anchor.classList.add("open-new-window");
      anchor.innerHTML = url;
      wrap.appendChild(anchor);
      return wrap.innerHTML;
    });
  }

  static addClassToAnchor(content: string): string {
    if (content === "" || content === undefined) {
      return "";
    }
    const parser = new DOMParser();
    return content.replace(/<a [^>]+>([^<]+)<\/a>/ig, (aHref) => {
      const htmlDoc = parser.parseFromString(aHref, "text/html");
      const aTag = htmlDoc.getElementsByTagName("a")[0];
      aTag.setAttribute("class", "open-new-window");
      if (aTag.getAttribute("href") !== null) {
        const url = aTag.getAttribute("href");
        if (!url.startsWith("http") && !url.startsWith("https")) {
          const href = "http://" + url;
          aTag.setAttribute("href", href);
        }
      }
      return htmlDoc.body.innerHTML;
    });
  }

  static addSlashToUrl(url: string) {
    if (url.endsWith("/")) {
      return url;
    }
    return url + "/";
  }

  static getAttachmentLocalAPIURL(url: string) {
    if (url) {
      let regExp = new RegExp("^(http|https)://", "i");
      if (regExp.test(url)) {
        const rootURL = url.toString().replace(/^(.*\/\/[^\/?#]*).*$/,"$1");
        let serverUrl;
        if ( environment.isCordova || environment.isElectron) {
          serverUrl = localStorage.getItem("serverURL");
          const apiUrl = ( url.indexOf("api") !== -1) ? "" : "/api";
          if ( url.indexOf("token=") !== -1) {
            url = url.substring(0, url.indexOf("?"));
          }
          return this.addTokenToURL(url.toString().replace(rootURL, serverUrl + apiUrl));
        } else {
          return url.toString().replace(rootURL, "api");
        }
      } else {
        return url;
      }
    }
    return url;
  }

  static getPrivateOriginAttachmentLocalAPIURL(url: string) {
    let contentUrl = this.getAttachmentLocalAPIURL(url);
    if ( !environment.isCordova && !environment.isElectron) {
      contentUrl = window.location.origin + `/` + contentUrl;
    }
    return contentUrl;
  }

  static getAttachmentServerURL(url: string) {
    if (url) {
      if ( environment.isCordova || environment.isElectron) {
        return url;
      } else {
        const  serverURL = window.location.origin;
        return this.addTokenToURL(serverURL + "/" + url);
      }
    }
    return url;
  }

  static addTokenToURL(url) {
    let token = localStorage.getItem("token");
    if (url.indexOf("token=") > -1) {
      return url;
    }
    if (url.indexOf("?") !== -1) {
      return url + "&token=" + token;
    } else {
      return url + "?token=" + token;
    }
  }

  static bytesToSize(bytes, decimals = 2) {
    if (bytes === 0) return "0 Bytes";

    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ["bytes", "kb", "mb", "gb", "tb", "pb", "eb", "zb", "yb"];

    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
  }

  static getPlainText(text: string) {
    if (!text) {
      return "";
    }
    return htmlToText(text.replace(/<img[^>]*>/g,""), {});
  }

  static getPlainTextWithOpts(text: string, options?: any) {
    if (!text) {
      return "";
    }
    return htmlToText(text.replace(/<img[^>]*>/g, ""), options);
  }

  static bytesToMegaBytes(bytes) {
    return bytes / (1024 * 1024);
  }

  static bytesToKiloBytes(bytes) {
    return bytes / 1024;
  }

  static getSendInlineImages(content: any) {
    const parser = new DOMParser();
    const updatedContent = this.removeSmartObjectIcons(content);
    const htmlDoc = parser.parseFromString(updatedContent, "text/html");
    const images = htmlDoc.getElementsByTagName("img");
    if (images.length > 0) {
      for (let i = 0; i < images.length; i++) {
        const img = images[i];
        const src = img.getAttribute("src");
        if (src !== null && img.getAttribute("src").indexOf("api/attachment") !== -1) {
          let endPoint = (src.indexOf("?token=") !== -1) ? src.indexOf("?token=") : src.length;
          const newSrc = src.substring(src.indexOf("api/attachment"), endPoint);
          img.setAttribute("src", newSrc);
        }
      }
      return htmlDoc.body.innerHTML;
    }
    return content;
  }

  static removeInlineImage(content: any, removeId: number) {
    if (!content) {
      return "";
    }
    const parser = new DOMParser();
    const htmlDoc = parser.parseFromString(content, "text/html");
    const images = htmlDoc.getElementsByTagName("img");
    if (images.length > 0) {
      for (let i = 0; i < images.length; i++) {
        const img = images[i];
        const src = img.getAttribute("src");
        if (src !== null && src.indexOf(removeId + "") !== -1) {
          htmlDoc.body.innerHTML = htmlDoc.body.innerHTML.replace(img.outerHTML, "");
        }
      }
      return htmlDoc.body.innerHTML;
    }
    return content;
  }

  static removeSmartObjectIcons(content): string {
    const parser = new DOMParser();
    const htmlDoc = parser.parseFromString(content, "text/html");
    const smartObjects = htmlDoc.querySelectorAll("s[data-type=\"SMART_OBJECTS\"]");
    smartObjects.forEach(obj => {
      obj.querySelectorAll(".smart-object-icon").forEach(icon => icon.remove());
    });
    return htmlDoc.body.innerHTML as string;
  }

  static getViewInlineImages(content: any, size?: any, is_iom?: boolean) {
    const isCordovaOrElectron = environment.isCordova || environment.isElectron;
    const parser = new DOMParser();
    const htmlDoc = parser.parseFromString(content, "text/html");
    const images = htmlDoc.getElementsByTagName("img");
    if (images && images.length > 0) {
      for (let i = 0; i < images.length; i++) {
        const img = images[i];
        const src = img.getAttribute("src");
        if (src !== null && img.getAttribute("src").indexOf("api/attachment") !== -1) {
          if (isCordovaOrElectron) {
            const serverURL = localStorage.getItem("serverURL");
            let newSrc;
            if (src.indexOf("?") !== -1) {
              newSrc = src.substring(src.indexOf("api/attachment"), src.indexOf("?"));
            } else {
              newSrc = src.substring(src.indexOf("api/attachment"));
            }
            const fullUrl = this.addTokenToURL(serverURL + "/" + newSrc);
            if (size) {
              img.setAttribute("src", this.getThumbnailURL(is_iom ? src : fullUrl, size));
            } else {
              img.setAttribute("src", is_iom ? src : fullUrl);
            }
          } else {
            let newSrc;
            if (src.indexOf("?") !== -1) {
              newSrc = src.substring(src.indexOf("api/attachment"), src.indexOf("?"));
            } else {
              newSrc = src.substring(src.indexOf("api/attachment"));
            }
            if (size) {
              img.setAttribute("src", this.getThumbnailURL(is_iom ? src : newSrc, size));
            } else {
              img.setAttribute("src", is_iom ? src : newSrc);
            }
          }
        }
      }
      return htmlDoc.body.innerHTML;
    }
    return content;
  }

  static getBaseOriginUrl() {
    if ( environment.isCordova || environment.isElectron) {
      return localStorage.getItem("serverURL");
    } else {
      return window.location.protocol + "//" + window.location.host;
    }
  }

  static loadExternalScript(scriptUrl: string) {
    return new Promise(resolve => {
      let scriptElement = document.createElement("script");
      scriptElement.src = scriptUrl;
      scriptElement.onload = resolve;
      document.body.appendChild(scriptElement);
      scriptElement = null;
    });
  }

  // static getThumbnailURL(url, size) {
  //   if (url.indexOf("?") !== -1) {
  //     return url +  "&size=" + size;
  //   } else {
  //     return url +  "?size=" + size;
  //   }
  // }

  static getThumbnailURL(url: string, size) {
    const queryParamsIndex = url.indexOf("?");
    if (queryParamsIndex !== -1) {
      const sizeIndex = url.indexOf("size=", queryParamsIndex);
      if (sizeIndex !== -1) {
        const nextParamIndex = url.indexOf("&", sizeIndex);
        if (nextParamIndex === -1) {
          url = url.substring(0, sizeIndex);
          return url + "size=" + size;
        } else {
          url = url.substring(0, sizeIndex) + url.substring(nextParamIndex + 1, url.length);
          return url + "&size=" + size;
        }
      }
      return url + "&size=" + size;
    } else {
      return url + "?size=" + size;
    }
  }

  static isStringHasImage(text: string): boolean {
    let hasImage: boolean = false;
    if (text !== undefined && text !== "" && text !== null) {
        const parser = new DOMParser();
        const htmlDoc = parser.parseFromString(text, "text/html");
        const images = htmlDoc.getElementsByTagName("img");
        if (images.length > 0) {
            hasImage = true;
        }
    }
    return hasImage;
  }

  static convertImageToLink(text: string): string {
    if (text !== undefined && text !== "" && text !== null) {
        const parser = new DOMParser();
        const htmlDoc = parser.parseFromString(text, "text/html");
        const images = htmlDoc.getElementsByTagName("img");
        for ( let i = 0; i < images.length; i++) {
            const img = images[i];
            img.remove();
        }
        return htmlDoc.body.innerHTML;
    }
    return text;
  }

  static getMappedAttachments(attachments: CommentItem["attachments"] = [], iom?: boolean): any[] {
    return attachments.map(attachment => ({
      ...attachment,
      content_url: iom ? attachment?.content_url : CommonUtil.getAttachmentLocalAPIURL(attachment?.content_url),
      thumbnail_url: iom ? attachment?.thumbnail_url : CommonUtil.getAttachmentLocalAPIURL(attachment?.thumbnail_url)
    }));
  }

  static getMappedCommentsForMention(comments: CommentItem[] = []) {
    return [...comments].map((comment: CommentItem) => ({
      ...comment,
      hasPrevious: false,
      hasNext: comment.replies?.length > 0,
      replies: comment?.replies ? CommonUtil.getMappedCommentsForMention(comment.replies) : [],
    }));
  }

  static getMappedComments(comments: CommentItem[] = [], is_iom?: boolean) {
    return [...comments].map((comment: CommentItem) => ({
      ...comment,
      replies: comment?.replies ? CommonUtil.getMappedComments(comment.replies, is_iom) : [],
      attachments: comment?.attachments ? CommonUtil.getMappedAttachments(comment.attachments, is_iom) : [],
    }));
  }

  static getNestedComments(commentResponse: CommentResponse) {
    const levels = commentResponse.levels;
    const comments = commentResponse.comments;
    switch (levels) {
      case 1: {
        return comments;
      }
      case 2: {
        const nestedComments = [...comments].filter(v => !v.parent_comment_id);
        const childComments = comments.filter(v => !!v.parent_comment_id);
        if (childComments.length > 0) {
          const parentCommentId = childComments[0].parent_comment_id;
          const index = nestedComments.map(v => v.id).indexOf(parentCommentId);
          const hasPrevious = commentResponse.level2_offset > 0;
          const hasNext = commentResponse.level2_total_count > commentResponse.level2_offset + childComments.length;
          nestedComments[index] = { ...nestedComments[index], replies: childComments, hasPrevious, hasNext};
        }
        return  nestedComments;
      }
      case 3: {
        const nestedComments = [...comments].filter(v => !v.parent_comment_id);
        const childComments = [...comments].filter(v => !!v.parent_comment_id);
        if (childComments.length > 0) {
          const parentCommentId1 = childComments[0].parent_comment_id;
          const directChildren = [...childComments].filter(v => v.parent_comment_id === parentCommentId1);
          if (directChildren.length > 0) {
            const index1 = nestedComments.map(v => v.id).indexOf(parentCommentId1);
            const grandChildren = [...childComments].filter(v => v.parent_comment_id !== parentCommentId1);
            if (grandChildren.length > 0) {
              const parentCommentId2 = grandChildren[0].parent_comment_id;
              const index2 = directChildren.map(v => v.id).indexOf(parentCommentId2);
              const hasPrevious = commentResponse.level2_offset > 0;
              const hasNext = commentResponse.level2_total_count > commentResponse.level2_offset + childComments.length;
              directChildren[index2] = { ...directChildren[index2], replies: grandChildren, hasNext, hasPrevious};
            }
            const hasPrevious = commentResponse.level2_offset > 0;
            const hasNext = commentResponse.level2_total_count > commentResponse.level2_offset + childComments.length;
            nestedComments[index1] = { ...nestedComments[index1], replies: directChildren, hasNext, hasPrevious};
          }
        }
        return  nestedComments;
      }
      default: {
        return comments;
      }
    }
  }

  static getChildOffsets(commentResponse: CommentResponse) {
    const childOffsets = {};
    if (Object.keys(commentResponse).includes("level2_offset")) {
      let parentCommentId1;
      const childComments = [...commentResponse.comments].filter(v => !!v.parent_comment_id);
      if (childComments.length > 0) {
        parentCommentId1 = childComments[0].parent_comment_id;
      }
      childOffsets[parentCommentId1] = {offset: commentResponse.level2_offset, total_count: commentResponse.level2_total_count};

      if (Object.keys(commentResponse).includes("level3_offset") && parentCommentId1) {
        let parentCommentId2;
        const grandChildComments = [...childComments].filter(v => v.parent_comment_id !== parentCommentId1);
        if (grandChildComments.length > 0) {
          parentCommentId2 = childComments[0].parent_comment_id;
        }
        childOffsets[parentCommentId2] = {offset: commentResponse.level3_offset, total_count: commentResponse.level3_total_count};
      }
    }
    return childOffsets;
  }

  static loadExternalStyle(styleUrl: string, callback) {
    return new Promise((resolve) => {
      let styleElement = document.createElement("link");
      styleElement.id = "vncTalkTheme";
      styleElement.rel = "stylesheet";
      styleElement.type = "text/css";
      styleElement.href = styleUrl;
      styleElement.onload = function () {
        resolve(callback);
        LoggerService.info("[loadExternalStyle]", styleUrl);
      };
      document.head.appendChild(styleElement);
      styleElement = null;
    });
  }

  static getCalendarDateText(dateISO: string) {
    const NOW = new Date();
    const TODAY = startOfDay(NOW);
    const YESTERDAY = startOfDay(subDays(NOW, 1));

    const date = parseISO(dateISO);

    if (isSameDay(date, TODAY)) {
      return "today";
    }
    if (isSameDay(date, YESTERDAY)) {
      return "yesterday";
    }

    if (isWithinInterval(date, {start: startOfWeek(NOW), end: endOfWeek(NOW)})) {
      return "this_week";
    }

    const minusOneWeek = subWeeks(NOW, 1);
    if (isWithinInterval(date, {start: startOfWeek(minusOneWeek), end: endOfWeek(minusOneWeek)})) {
      return "last_week";
    }

    const monthDiff = Math.abs(differenceInMonths(NOW, date));
    if (monthDiff === 0) {
      return "this_month";
    }
    if (monthDiff === 1) {
      return "last_month";
    }

    const yearDiff = Math.abs(differenceInYears(NOW, date));
    if (yearDiff === 0) {
      return "this_year";
    } else {
      return "last_year";
    }
  }

  static compareChannelsArrays(p: Channel[], q: Channel[]) {
    return (p.length === q.length) &&
      (p.map(c => c.updated_on + c.pinned + c.subscribed + c.muted + c.sub_channels_count
        + c.is_public + c.archived + c.unread_topics_count + c.topics_count + c.favorited + c.deleted + c.avatar_url + c.avatarFailedToLoad).toString() ===
        q.map(c => c.updated_on + c.pinned + c.subscribed + c.muted + c.sub_channels_count
          + c.is_public + c.archived + c.unread_topics_count + c.topics_count + c.favorited + c.deleted + c.avatar_url + c.avatarFailedToLoad).toString());
  }

  static compareContactsArrays(p: any[], q: any[]) {
    return (p.length === q.length) &&
      (p.map(c => c.first_name + c.last_name + c.name + c.jid).toString() ===
        q.map(c => c.first_name + c.last_name + c.name + c.jid).toString());
  }

  static compareTopicsArrays(p: Topic[], q: Topic[]) {
    return  this.isEqual(p, q);
  }

  static comparePostsArray(p: Post[], q: Post[]) {
    return  this.isEqual(p, q);
  }

  static compareFollowersArray(p: Follow[], q: Follow[]) {
    return  this.isEqual(p, q);
  }

  static getPermission(permissions: any[] | null = null, toCheck: string) {
    const permission = permissions?.find((p) => p[toCheck]);
    return permission === null ? this.DEFAULT_PERMISSIONS[toCheck] : permission;
  }

  static isEqual(value, other) {

    // Get the value type
    const type = Object.prototype.toString.call(value);

    // If the two objects are not the same type, return false
    if (type !== Object.prototype.toString.call(other)) return false;

    // If items are not an object or array, return false
    if (["[object Array]", "[object Object]"].indexOf(type) < 0) return false;

    // Compare the length of the length of the two items
    const valueLen = type === "[object Array]" ? value.length : Object.keys(value).length;
    const otherLen = type === "[object Array]" ? other.length : Object.keys(other).length;
    if (valueLen !== otherLen) return false;

    // Compare two items
    const compare = (item1, item2) => {

      // Get the object type
      const itemType = Object.prototype.toString.call(item1);

      // If an object or array, compare recursively
      if (["[object Array]", "[object Object]"].indexOf(itemType) >= 0) {
        if (!this.isEqual(item1, item2)) return false;
      }

      // Otherwise, do a simple comparison
      else {

        // If the two items are not the same type, return false
        if (itemType !== Object.prototype.toString.call(item2)) return false;

        // Else if it"s a function, convert to a string and compare
        // Otherwise, just compare
        if (itemType === "[object Function]") {
          if (item1.toString() !== item2.toString()) return false;
        } else {
          if (item1 !== item2) return false;
        }

      }
    };

    // Compare properties
    if (type === "[object Array]") {
      for (let i = 0; i < valueLen; i++) {
        if (compare(value[i], other[i]) === false) return false;
      }
    } else {
      for (const key in value) {
        if (value.hasOwnProperty(key)) {
          if (compare(value[key], other[key]) === false) return false;
        }
      }
    }

    // If nothing failed, return true
    return true;

  }

  static compareObjects(p: any, q: any) {
    try {
      let p1 = JSON.stringify(p);
      let q1 = JSON.stringify(q);
      if (p1 === q1) {
        return true;
      }
    } catch (e) {
      LoggerService.error("error in serializing objects: ", e);
    }
    return false;
  }

  static getTopicAvatar(topic: Topic): string {
    const attachment = topic?.attachments?.filter(attachment => attachment?.is_header === true)?.[0];
    if (attachment) {
      return CommonUtil.getAttachmentLocalAPIURL(attachment?.thumbnail_url);
    } else {
      return topic?.default_cover_url;
    }
  }

  static getVncDirectoryAttachemntURL(avatar: string): string {
    let vncDirectoryURL = "https://vncdirectory.dev.vnc.de/images/topic_covers/";
    return vncDirectoryURL + avatar;
  }

  static truncateTextWithElipses(text, length) {
    if (text && text.length > length) {
      return text.substring(0, length) + "...";
    }
    return text;
  }

  static linkifyHTML(inputText: string): string {
    if (!inputText) {
        return "";
    }
    return linkifyHtml(inputText, {
        defaultProtocol: "http",
        className: "open-new-window",
        target: {
            url: "_blank",
            email: null
        },
        formatHref: (href, type) => {
            if (type !== "email") {
                return href;
            }
            return "javascript:void(0)";
        },
        tagName: (href, type) => {
            if (type !== "email") {
                return "a";
            }
            return "span";
        }
    });
  }

  static getContentByUrl(url: string): Observable<string> {
    LoggerService.info("[CommonUtil][getContentByUrlSync]", url);

    const response = new Subject<string>();

    try {

      fetch(url, {
        method: "GET",
        mode: "cors",
        headers: { "Sec-Fetch-Site": "none" }
      }).then(async res => {
        const restext = await res.text();
        response.next(restext);
      });
    } catch (error) {
      LoggerService.error("[CommonUtil][getContentByUrl] error: ", error);
      response.error(error);
    }

    return response.asObservable().pipe(take(1));
  }

  static getLatestDevApkVersion(appName: string): Observable<string> {
    const fileNameRexp = new RegExp(appName + "-" + APP_VERSION_REGEXP + "\.apk", "gm");

    const response = new Subject<string>();
    let APK_URL = "https://newpackages.vnc.biz/apk/";
    CommonUtil.getContentByUrl(APK_URL).subscribe(content => {

      if (!!content) {
        let matches = content.match(fileNameRexp);
        if (matches) {
          const latestBuild = matches[matches.length - 1];
          const versionRegexp = new RegExp(APP_VERSION_REGEXP, "gm");
          const apkVersion = latestBuild.match(versionRegexp);
          LoggerService.info("[CommonUtil][getLatestDevApkVersion] Latest DEV version: ", apkVersion[0]);
          response.next(apkVersion[0]);
        } else {
          // eslint-disable-next-line no-console
          console.warn("[CommonUtil][getLatestDevApkVersion] Something went wrong during check version from FTP");
          response.next(null);
        }
      } else {
        response.next(null);
      }
    });

    return response.asObservable().pipe(take(1));
  }

  static removeImageFromHTML(content): string {
    if (!!content && content !== "") {
      return content.replace(/<img[^>]*>/g,"").replace(/<iframe[^>]*>/g,"");
    }
    return "";
  }

  static getInlineImages(content): any {
    const inlineAttachments: any [] = [];
    const images = this.getViewInlineImages(content);
    const parser = new DOMParser();
    const htmlDoc = parser.parseFromString(images, "text/html");
    const imgs = htmlDoc.getElementsByTagName("img");
    if (imgs && imgs.length > 0) {
      for (let i = 0; i < imgs.length; i++) {
        const img = imgs[i];
        const src = img.getAttribute("src");
        inlineAttachments.push(src);
      }
  }
  return inlineAttachments;
 }
  static isHttpResponse<T>(event: HttpEvent<T>): event is HttpResponse<T> {
    return event.type === HttpEventType.Response;
  }

  static isHttpProgressEvent(event: HttpEvent<unknown>): event is HttpProgressEvent {
    return (
      event.type === HttpEventType.DownloadProgress ||
      event.type === HttpEventType.UploadProgress
    );
  }

  static iOSversion() {
    if (/iP(hone|od|ad)/.test(navigator.userAgent)) {
      const v = (navigator.userAgent).match(/OS (\d+)_(\d+)_?(\d+)?/);
      return parseInt(v[1], 10);
    }
  }

  static calculateState = (upload: Upload, event: HttpEvent<unknown>): Upload => {
    if (CommonUtil.isHttpProgressEvent(event)) {
      return {
        progress: event.total
          ? Math.round((100 * event.loaded) / event.total)
          : upload.progress,
        state: "IN_PROGRESS",
      };
    }
    if (CommonUtil.isHttpResponse(event)) {
      return {
        progress: 100,
        state: "DONE",
      };
    }
    return upload;
  };

  static parseRedmineMentions(prefix: string, text: string): string[] {
    if (!text) {
      return [];
    }
    const mentionsRegex = new RegExp(prefix + "#([a-zA-Z0-9\_\.\-]+@[a-zA-Z0-9\_\.\-]+)", "gim");
    // LoggerService.info("[parseMentions]", mentionsRegex, text);
    let matches = text.match(mentionsRegex);
    if (matches && matches.length) {
        return matches.filter(v => CommonUtil.isValidEmail(v));
    } else {
        return [];
    }
  }

  static getVideosLinkFromEditor(content: any) {
    const parser = new DOMParser();
    const htmlDoc = parser.parseFromString(content, "text/html");
    const iframes = htmlDoc.getElementsByTagName("iframe");
    let iframesList: string = "";
    if (iframes.length > 0) {
      for (let i = 0; i < iframes.length; i++) {
        const frame = iframes[i];
        const src = frame.classList.contains("ql-video");
        if (src !== null) {
          iframesList += frame.outerHTML;
        }
      }
      return iframesList;
    }
    return iframesList;
  }

  static isChannelFileURL(url: string): boolean {
    if (url.indexOf("/attachments/download/") !== -1) {
      return true;
    }
    return false;
  }

  static mapPost(post) {
    const contentData = post?.content;
    post.content = CommonUtil.linkifyHTML(contentData);
    post.inlineAttachments = [];
    if (!!post?.attachments && post?.attachments.length > 0) {
      post?.attachments.map(a => {
        a.content_url =  CommonUtil.getAttachmentLocalAPIURL(a?.content_url);
          a.thumbnail_url = CommonUtil.getAttachmentLocalAPIURL(CommonUtil.getThumbnailURL(a?.thumbnail_url, 800));
        post.inlineAttachments.push(a);
      });
    } else {
      post.fullUrl = post?.data?.fullUrl || "";
      post.iframeVideos = post?.data?.iframeVideos;
      post.isUrlPreview = !!post?.original_post ? post?.original_post?.data?.isUrlPreview : post?.data?.isUrlPreview;
      post.messageUrlPreviewData = !!post?.original_post ? post?.original_post?.data?.messageUrlPreviewData : post?.data?.messageUrlPreviewData;
    }
    return { ...post };
  }

  static getDatePattern(locale) {
    const formatter = new Intl.DateTimeFormat(locale).formatToParts();
    return formatter.map((e) => {
        switch (e.type) {
          case "month":
            return "MM";
          case "day":
            return "dd";
          case "year":
            return "yyyy";
          default:
            return e.value;
        };
      }).join("");
  }

  static isScrolledIntoView(el) {
    var rect = el?.getBoundingClientRect();
    return (rect?.top >= 0) && (rect?.bottom <= window?.innerHeight);
  }

  static findKey(object, predicate) {
    for (let key in object) {
        let value = object[key];
        let predicateReturnValue = predicate(value);
        if (predicateReturnValue) {                   // just take the value
            return key;                               // return key
        }
    }
  }

  static uniq(array: any[]) {
    return Array.from(new Set(array));
  }

  static sortBy(key) {
    return (a, b) => (a[key] > b[key]) ? 1 : ((b[key] > a[key]) ? -1 : 0);
  }

  static sortByLowerCase(key) {
    return (a, b) => (a[key].toLowerCase() > b[key].toLowerCase()) ? 1 : ((b[key].toLowerCase() > a[key].toLowerCase()) ? -1 : 0);
  }


  static isMergeableObject(val) {
    const nonNullObject = val && typeof val === "object";
    return nonNullObject &&
        Object.prototype.toString.call(val) !== "[object RegExp]" &&
        Object.prototype.toString.call(val) !== "[object Date]";
  }

  static emptyTarget(val) {
    return Array.isArray(val) ? [] : {};
  }

  static cloneIfNecessary(value, optionsArgument) {
    const clone = optionsArgument && optionsArgument.clone === true;
    return (clone && this.isMergeableObject(value)) ? this.merge(this.emptyTarget(value), value, optionsArgument) : value;
  }

  static defaultArrayMerge(target, source, optionsArgument) {
    var destination = target.slice();
    source.forEach(function (e, i) {
      if (typeof destination[i] === "undefined") {
        destination[i] = this.cloneIfNecessary(e, optionsArgument);
      } else if (this.isMergeableObject(e)) {
        destination[i] = this.merge(target[i], e, optionsArgument);
      } else if (target.indexOf(e) === -1) {
        destination.push(this.cloneIfNecessary(e, optionsArgument));
      }
    });
    return destination;
  }

  static mergeObject(target, source, optionsArgument) {
    const destination = {};
    if (this.isMergeableObject(target)) {
      Object.keys(target).forEach(function (key) {
        destination[key] = this.cloneIfNecessary(target[key], optionsArgument);
      });
    }
    Object.keys(source).forEach(function (key) {
      if (!this.isMergeableObject(source[key]) || !target[key]) {
        destination[key] = this.cloneIfNecessary(source[key], optionsArgument);
      } else {
        destination[key] = this.merge(target[key], source[key], optionsArgument);
      }
    });
    return destination;
  }

  static merge(target, source, optionsArgument) {
    var array = Array.isArray(source);
    var options = optionsArgument || { arrayMerge: this.defaultArrayMerge };
    var arrayMerge = options.arrayMerge || this.defaultArrayMerge;
    if (array) {
      return Array.isArray(target) ? arrayMerge(target, source, optionsArgument) : this.cloneIfNecessary(source, optionsArgument);
    } else {
      return this.mergeObject(target, source, optionsArgument);
    }
  }

  static uniqBy(arr, predicate){
    const cb = typeof predicate === "function" ? predicate : (o) => o[predicate];
    if (typeof arr === "undefined") {
      return [];
    }
    return [...arr.filter(v => !!v).reduce((map, item) => {
      const key = (item === null || item === undefined) ?
        item : cb(item);

      map.has(key) || map.set(key, item);

      return map;
    }, new Map()).values()];
  }

  static pick(object, keys) {
    return keys.reduce((obj, key) => {
       if (object && object.hasOwnProperty(key)) {
          obj[key] = object[key];
       }
       return obj;
     }, {});
  }

  static smartSubstr(string: string, length: number) {
    if (!string) {
      return "";
    }
    const noHTML = string.replace(/<[^>]*>?/gm, " ").replace(/\s+/g, " ");
    if (noHTML?.length <= length) {
      return string;
    }
    const subStringNoHTML = noHTML.substr(0, noHTML.indexOf(" ", length));
    const words = subStringNoHTML.split(" ");
    let outPutString = "";
    const wordIndexes = [];

    words.forEach((word, key) => {

      if (key == 0) {

        outPutString += string.substr(0, string.indexOf(word) + word.length);
        wordIndexes[key] = string.indexOf(word) + word.length;

      } else {

        let i = wordIndexes[key - 1];

        outPutString += string.substring(i, string.indexOf(word, i) + word.length);
        wordIndexes[key] = string.indexOf(word, i) + word.length;

      }
    });

    return outPutString;

  }

  static rmap(obj, pobj) {
    return Object.entries(obj).reduce((acc, [key, value]) => {
        if (!(key in pobj)) return { ...acc, [key]: value };
        if (typeof pobj[key] === "object" && pobj[key] !== null && !Array.isArray(pobj[key])) {
            return { ...acc, [key]: this.rmap(obj[key], pobj[key]) };
        } else {
            return { ...acc, [key]: pobj[key] };
        }
    }, {});
  };

  static isPreviewAvailableForType(fileType: string):boolean{
    fileType = fileType.toLowerCase();
    return this.FILE_TYPES_AVAILABLE_FOR_PREVIEW.includes(fileType);
  }

  static convertXmlToJson(xmlString) {
    let obj = {};
    let xml: any;
    if (typeof xmlString !== "string") {
      xml = xmlString;
    } else {
      xml = new DOMParser().parseFromString(xmlString, "text/xml");
    }
    if (xml.nodeType == 1) {
      // element
      // do attributes
      if (xml.attributes.length > 0) {
        obj["@attributes"] = {};
        for (let j = 0; j < xml.attributes.length; j++) {
          const attribute = xml.attributes.item(j);
          obj["@attributes"][attribute.nodeName] = attribute.nodeValue;
        }
      }
    } else if (xml.nodeType == 3) {
      // text
      obj = xml.nodeValue;
    }

    // do children
    // If all text nodes inside, get concatenated text from them.
    const textNodes = [].slice.call(xml.childNodes).filter((node) => {
      return node.nodeType === 3;
    });
    if (xml.hasChildNodes() && xml.childNodes.length === textNodes.length) {
      obj = [].slice.call(xml.childNodes).reduce((text, node) => {
        return text + node.nodeValue;
      }, "");
    } else if (xml.hasChildNodes()) {
      for (let i = 0; i < xml.childNodes.length; i++) {
        let item = xml.childNodes.item(i);
        let nodeName = item.nodeName;
        if (typeof obj[nodeName] == "undefined") {
          obj[nodeName] = CommonUtil.convertXmlToJson(item);
        } else {
          if (typeof obj[nodeName].push == "undefined") {
            var old = obj[nodeName];
            obj[nodeName] = [];
            obj[nodeName].push(old);
          }
          obj[nodeName].push(CommonUtil.convertXmlToJson(item));
        }
      }
    }
    return obj;
  }
}


export enum MediaType {
  IMAGE = "image",
  VOICE_MESSAGE = "voice-messages",
  ATTACHMENTS = "attachments",
  VIDEOS = "videos",
  DOCUMENTS = "docs",
  ALL = "all",
  PDF = "pdf",
  DOC = "doc",
  TEXT = "txt",
  EXCEL = "excel",
  PPT = "ppt",
  RAR = "rar",
  ZIP = "zip"
}

export enum MediaLongPressAction {
  VIEW = "view",
  SHARE = "share",
  DOWNLOAD = "download",
  DELETE = "delete"
}
